import {useLazyQuery, useQuery} from '@apollo/client'
import React, {useEffect, useRef, useState} from 'react'
import {Form, Button, Col, Modal, Row, Alert, Spinner, Nav, OverlayTrigger, Tooltip} from 'react-bootstrap'
import {useNavigate} from 'react-router-dom'
import {GET_ZIP_INFO} from '../../../gql/queries/leadsQuery'
import {GET_ALL_STATES_EU_LIST} from '../../../gql/queries/statesEUQuery'
import {CHECK_US_LOCATION} from '../../../gql/queries/authQuery'
import {IResZipCode} from '../../../types/quoteTypes'
import {NodeStateEU} from '../../../types/statesEU'
import {toast} from 'react-toastify'
import {GENDER_OPTIONS, INIT_PEOPLE, SIZE_PAGE_QUICK_QUOTE, BACKEND_URL} from '../../../helpers/consts'
import {calculateAge, getYearFromEffectiveDate} from '../../../helpers'
import {DateInput} from 'app/tfi/components/DateInput'
import {IForm} from '../../oneAddQuote/oneQuoteTypes'

interface IPerson {
  age: string
  dob: string
  gender: string
  isPregnant: boolean
  pregnantWith: string
  isParent: boolean
  usesTobacco: boolean
  lastTobaccoUseDate: string
  hasMec: boolean
  aptcEligible: boolean
  utilizationLevel: string
  relationship: string
  doesNotCohabitate: string
  currentEnrollment?: null | {
    planId: string
    effectiveDate: string
    usesTobacco: boolean
  }
  id: number
}

interface IModalAddQProps {
  isOpen: boolean
  onClose: () => void
  info?: {
    id: string
    dateBirthLead: string | null | undefined
  }
}

const NICOTINE_OPTIONS = [
  'None',
  'Cigarettes',
  'Cigarettes + Other Nicotine Products',
  'Occasional pipe/cigar use only',
  'Other Nicotine Products',
]

const TERM_OPTIONS = [10, 15, 20, 25, 30, 35, 40]

const MED_SUPP_PLAN_CODES = ['A', 'B', 'C', 'D', 'F', 'HDF', 'G', 'HDG', 'K', 'L', 'M', 'N']

const FEX_COVERAGE_TYPES = ['Level', 'Graded/Modified', 'Guaranteed', 'Limited Pay', 'SPWL']

const PAYMENT_TYPES = ['Bank Draft/EFT', 'Direct Express', 'Credit Card', 'Debit Card']
const FEX_PAYMENT_TYPES = PAYMENT_TYPES

const LIFE_TOKEN = 'sXmdEZFxqnZN3uDYVxN6WuHuz6LujGpwI-yH6fq4'

export const ModalAddQuickQuote: React.FC<IModalAddQProps> = ({isOpen, onClose, info}) => {
  const [activeTab, setActiveTab] = useState<'aca' | 'life' | 'medsupp' | 'dvh' | 'hi'>('aca')
  const [lifeSubTab, setLifeSubTab] = useState<'term' | 'fex' | 'iul'>('term')
  const [lifeView, setLifeView] = useState<'form' | 'results'>('form')
  const [lifeLoading, setLifeLoading] = useState(false)
  const [lifeQuotes, setLifeQuotes] = useState<any[]>([])
  const [lifeExpanded, setLifeExpanded] = useState<Set<number>>(new Set())

  const [sharedLifeForm, setSharedLifeForm] = useState({
    quoteMode: 'face' as 'face' | 'premium',
    faceAmount: '',
    premiumAmount: '',
    sex: 'Male',
    state: '',
    dob: '',
    age: '',
    tobacco: 'None',
  })

  const [lifeForm, setLifeForm] = useState({
    term: '20',
    returnOfPremium: false,
    coverageType: 'Level',
  })

  const handleLifeSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLifeLoading(true)

    const payload: Record<string, any> = {
      sex: sharedLifeForm.sex,
      state: sharedLifeForm.state,
      tobacco: sharedLifeForm.tobacco,
      term: lifeForm.term,
      returnOfPremium: lifeForm.returnOfPremium,
      coverageType: lifeForm.coverageType,
    }

    if (sharedLifeForm.dob) {
      const [y, m, d] = sharedLifeForm.dob.split('-')
      payload.year = y
      payload.month = m
      payload.day = d
    } else {
      payload.age = Number(sharedLifeForm.age)
    }

    if (sharedLifeForm.quoteMode === 'face') {
      payload.faceAmount = Number(sharedLifeForm.faceAmount)
    } else {
      payload.premiumAmount = Number(sharedLifeForm.premiumAmount)
    }

    try {
      const token = localStorage.getItem('TOKEN')
      const res = await fetch(`${BACKEND_URL}/api/integrations/term-quote`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      })
      if (!res.ok) throw new Error(`Error ${res.status}`)
      const data = await res.json()
      setLifeQuotes(data.quotes || [])
      setLifeView('results')
    } catch (err) {
      toast.error('Error fetching life insurance quotes')
    } finally {
      setLifeLoading(false)
    }
  }
  // Med Supp state
  const [medSuppView, setMedSuppView] = useState<'form' | 'results'>('form')
  const [medSuppLoading, setMedSuppLoading] = useState(false)
  const [medSuppQuotes, setMedSuppQuotes] = useState<any[]>([])
  const [medSuppExpanded, setMedSuppExpanded] = useState<Set<number>>(new Set())
  const [medSuppForm, setMedSuppForm] = useState({
    planCode: 'G',
    zipcode: '',
    countyName: '',
    state: '',
    paymentType: 'Bank Draft/EFT',
    effectiveDate: (() => {
      const d = new Date()
      return new Date(d.getFullYear(), d.getMonth() + 1, 1).toISOString().split('T')[0]
    })(),
  })

  const handleMedSuppZipLookup = () => {
    if (medSuppForm.zipcode.length < 3) return
    setMedSuppForm((p) => ({...p, countyName: '', state: ''}))
    gqlGetMedSuppZip({variables: {zipCode: medSuppForm.zipcode}})
  }

  const handleMedSuppSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setMedSuppLoading(true)

    const payload: Record<string, any> = {
      toolkit: 'MEDSUPP',
      planCode: medSuppForm.planCode,
      zipcode: medSuppForm.zipcode,
      countyName: medSuppForm.countyName,
      state: medSuppForm.state,
      sex: sharedLifeForm.sex,
      tobacco: sharedLifeForm.tobacco,
      paymentType: medSuppForm.paymentType,
    }

    if (sharedLifeForm.dob) {
      const [y, m, d] = sharedLifeForm.dob.split('-')
      payload.year = y
      payload.month = m
      payload.day = d
    } else {
      payload.age = Number(sharedLifeForm.age)
    }

    if (medSuppForm.effectiveDate) {
      const [ey, em, ed] = medSuppForm.effectiveDate.split('-')
      payload.effectiveYear = ey
      payload.effectiveMonth = em
      payload.effectiveDay = ed
    }

    try {
      const token = localStorage.getItem('TOKEN')
      const res = await fetch(`${BACKEND_URL}/api/integrations/term-quote`, {
        method: 'POST',
        headers: {'Content-Type': 'application/json', Authorization: `Bearer ${token}`},
        body: JSON.stringify(payload),
      })
      if (!res.ok) throw new Error(`Error ${res.status}`)
      const data = await res.json()
      setMedSuppQuotes(data.quotes || [])
      setMedSuppView('results')
    } catch {
      toast.error('Error fetching Medicare Supplement quotes')
    } finally {
      setMedSuppLoading(false)
    }
  }

  // FEX state
  const [fexView, setFexView] = useState<'form' | 'results'>('form')
  const [fexLoading, setFexLoading] = useState(false)
  const [fexQuotes, setFexQuotes] = useState<any[]>([])
  const [fexExpanded, setFexExpanded] = useState<Set<number>>(new Set())
  const [fexForm, setFexForm] = useState({
    coverageType: 'Level',
    paymentType: 'Bank Draft/EFT',
  })

  const handleFexSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setFexLoading(true)

    const payload: Record<string, any> = {
      toolkit: 'FEX',
      coverageType: fexForm.coverageType,
      paymentType: fexForm.paymentType,
      sex: sharedLifeForm.sex,
      state: sharedLifeForm.state,
      tobacco: sharedLifeForm.tobacco,
    }

    if (sharedLifeForm.dob) {
      const [y, m, d] = sharedLifeForm.dob.split('-')
      payload.year = y
      payload.month = m
      payload.day = d
    } else {
      payload.age = Number(sharedLifeForm.age)
    }

    if (sharedLifeForm.quoteMode === 'face') {
      payload.faceAmount = Number(sharedLifeForm.faceAmount)
    } else {
      payload.premiumAmount = Number(sharedLifeForm.premiumAmount)
    }

    try {
      const token = localStorage.getItem('TOKEN')
      const res = await fetch(`${BACKEND_URL}/api/integrations/term-quote`, {
        method: 'POST',
        headers: {'Content-Type': 'application/json', Authorization: `Bearer ${token}`},
        body: JSON.stringify(payload),
      })
      if (!res.ok) throw new Error(`Error ${res.status}`)
      const data = await res.json()
      setFexQuotes(data.quotes || [])
      setFexView('results')
    } catch {
      toast.error('Error fetching Final Expense quotes')
    } finally {
      setFexLoading(false)
    }
  }

  // IUL state
  const [iulView, setIulView] = useState<'form' | 'results'>('form')
  const [iulLoading, setIulLoading] = useState(false)
  const [iulQuotes, setIulQuotes] = useState<any[]>([])
  const [iulExpanded, setIulExpanded] = useState<Set<number>>(new Set())
  // IUL has no unique fields beyond the shared state; kept as placeholder for future unique IUL fields

  const handleIulSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIulLoading(true)
    const payload: Record<string, any> = {
      toolkit: 'IUL',
      sex: sharedLifeForm.sex,
      state: sharedLifeForm.state,
      tobacco: sharedLifeForm.tobacco,
    }
    if (sharedLifeForm.dob) {
      const [y, m, d] = sharedLifeForm.dob.split('-')
      payload.year = y
      payload.month = m
      payload.day = d
    } else {
      payload.age = Number(sharedLifeForm.age)
    }
    if (sharedLifeForm.quoteMode === 'face') {
      payload.faceAmount = Number(sharedLifeForm.faceAmount)
    } else {
      payload.premiumAmount = Number(sharedLifeForm.premiumAmount)
    }
    try {
      const token = localStorage.getItem('TOKEN')
      const res = await fetch(`${BACKEND_URL}/api/integrations/term-quote`, {
        method: 'POST',
        headers: {'Content-Type': 'application/json', Authorization: `Bearer ${token}`},
        body: JSON.stringify(payload),
      })
      if (!res.ok) throw new Error(`Error ${res.status}`)
      const data = await res.json()
      setIulQuotes(data.quotes || [])
      setIulView('results')
    } catch {
      toast.error('Error fetching IUL quotes')
    } finally {
      setIulLoading(false)
    }
  }

  const [gqlGetMarket, {data, loading: loadingZipCode, error}] = useLazyQuery<IResZipCode>(
    GET_ZIP_INFO,
    {
      fetchPolicy: 'no-cache',
    }
  )

  const [gqlCheckUsLocation, {loading: checkingUsLocation}] = useLazyQuery<{isUsLocation: boolean}>(
    CHECK_US_LOCATION,
    {
      fetchPolicy: 'no-cache',
    }
  )
  const [showOutsideUsModal, setShowOutsideUsModal] = useState(false)

  const [gqlGetMedSuppZip, {data: dataMedSuppZip, loading: loadingMedSuppZip}] = useLazyQuery<IResZipCode>(
    GET_ZIP_INFO,
    {fetchPolicy: 'no-cache'}
  )

  // DVH state
  const [dvhView, setDvhView] = useState<'form' | 'results'>('form')
  const [dvhLoading, setDvhLoading] = useState(false)
  const [dvhQuotes, setDvhQuotes] = useState<any[]>([])
  const [dvhCardData, setDvhCardData] = useState<Record<number, any>>({})
  const [dvhCardLoading, setDvhCardLoading] = useState<Record<number, boolean>>({})
  const [dvhCardPlan, setDvhCardPlan] = useState<Record<number, string>>({})
  const [dvhCardIndividuals, setDvhCardIndividuals] = useState<Record<number, string>>({})
  const [dvhCardDeductible, setDvhCardDeductible] = useState<Record<number, string>>({})
  const [dvhCardBenefit, setDvhCardBenefit] = useState<Record<number, string>>({})
  const [dvhCardRider, setDvhCardRider] = useState<Record<number, string>>({})
  const [dvhForm, setDvhForm] = useState({dob: '', age: '', zipcode: '', countyName: '', state: ''})

  const [hiView, setHiView] = useState<'form' | 'results'>('form')
  const [hiLoading, setHiLoading] = useState(false)
  const [hiQuotes, setHiQuotes] = useState<any[]>([])
  const [hiCardDays, setHiCardDays] = useState<Record<number, number>>({})
  const [hiCardDaily, setHiCardDaily] = useState<Record<number, number>>({})
  const [hiCardData, setHiCardData] = useState<Record<number, any>>({})
  const [hiCardLoading, setHiCardLoading] = useState<Record<number, boolean>>({})
  const [hiCardRiders, setHiCardRiders] = useState<Record<number, Record<string, number>>>({})
  const [hiCardExpanded, setHiCardExpanded] = useState<Set<number>>(new Set())
  const [hiForm, setHiForm] = useState({dob: '', age: '', sex: 'Male', state: '', paymentType: 'Bank Draft/EFT'})

  const [gqlGetDvhZip, {data: dataDvhZip, loading: loadingDvhZip}] = useLazyQuery<IResZipCode>(
    GET_ZIP_INFO,
    {fetchPolicy: 'no-cache'}
  )
  const handleDvhSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setDvhLoading(true)
    const payload: Record<string, any> = {}
    if (dvhForm.dob) {
      const [y, m, d] = dvhForm.dob.split('-')
      payload.year = y; payload.month = m; payload.day = d
    } else {
      payload.age = Number(dvhForm.age)
    }
    payload.zipcode = dvhForm.zipcode
    payload.state = dvhForm.state
    payload.toolkit = 'DVH'
    try {
      const token = localStorage.getItem('TOKEN')
      const res = await fetch(`${BACKEND_URL}/api/integrations/dvh-quote`, {
        method: 'POST',
        headers: {'Content-Type': 'application/json', Authorization: `Bearer ${token}`},
        body: JSON.stringify(payload),
      })
      if (!res.ok) throw new Error(`Error ${res.status}`)
      const data = await res.json()
      setDvhQuotes(data.quotes || data || [])
      setDvhCardData({})
      setDvhCardLoading({})
      setDvhCardPlan({})
      setDvhCardIndividuals({})
      setDvhCardDeductible({})
      setDvhCardBenefit({})
      setDvhCardRider({})
      setDvhView('results')
    } catch {
      toast.error('Error fetching DVH quotes')
    } finally {
      setDvhLoading(false)
    }
  }

  const fetchDvhCardQuote = async (
    idx: number,
    productName: string,
    planName: string,
    coveredIndividuals: string,
    deductible: string,
    calendarYearBenefit?: string,
  ) => {
    setDvhCardLoading((p) => ({...p, [idx]: true}))
    try {
      const token = localStorage.getItem('TOKEN')
      const payload: Record<string, any> = {
        toolkit: 'DVH',
        plan_name: planName,
        covered_individuals: coveredIndividuals,
        deductible,
        state: dvhForm.state,
        zipcode: dvhForm.zipcode,
        product_name: productName,
      }
      if (calendarYearBenefit) payload.calendar_year_benefit = calendarYearBenefit
      if (dvhForm.dob) {
        const [y, m, d] = dvhForm.dob.split('-')
        payload.year = Number(y)
        payload.month = Number(m)
        payload.day = Number(d)
      } else {
        payload.age = Number(dvhForm.age)
      }
      const res = await fetch(`${BACKEND_URL}/api/integrations/dvh-quote`, {
        method: 'POST',
        headers: {'Content-Type': 'application/json', Authorization: `Bearer ${token}`},
        body: JSON.stringify(payload),
      })
      if (!res.ok) throw new Error(`Error ${res.status}`)
      const data = await res.json()
      const quotes = data.quotes || data || []
      if (quotes.length > 0) {
        setDvhCardData((p) => ({
          ...p,
          [idx]: {
            ...quotes[0],
            ruleResult: {
              ...quotes[0].ruleResult,
              company_logo: p[idx]?.ruleResult?.company_logo || quotes[0].ruleResult?.company_logo,
            },
          },
        }))
        setDvhCardRider((p) => ({...p, [idx]: ''}))
      }
    } catch {
      toast.error('Error loading plan details')
    } finally {
      setDvhCardLoading((p) => ({...p, [idx]: false}))
    }
  }

  const handleHiSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setHiLoading(true)
    try {
      const token = localStorage.getItem('TOKEN')
      const payload: Record<string, any> = {
        toolkit: 'HI',
        sex: hiForm.sex,
        state: hiForm.state,
        paymentType: hiForm.paymentType,
        underwritingItems: [],
      }
      if (hiForm.dob) {
        const [y, m, d] = hiForm.dob.split('-')
        payload.year = y; payload.month = m; payload.day = d
      } else {
        payload.age = Number(hiForm.age)
      }
      const res = await fetch(`${BACKEND_URL}/api/integrations/term-quote`, {
        method: 'POST',
        headers: {'Content-Type': 'application/json', Authorization: `Bearer ${token}`},
        body: JSON.stringify(payload),
      })
      if (!res.ok) throw new Error(`Error ${res.status}`)
      const data = await res.json()
      setHiQuotes((data.quotes || []).filter((q: any) => q.hi_response))
      setHiCardDays({})
      setHiCardDaily({})
      setHiCardData({})
      setHiCardLoading({})
      setHiCardRiders({})
      setHiCardExpanded(new Set())
      setHiView('results')
    } catch {
      toast.error('Error fetching Hospital Indemnity quotes')
    } finally {
      setHiLoading(false)
    }
  }

  const fetchHiCardQuote = async (
    idx: number,
    companyId: string,
    days: number,
    dailyBenefit: number,
    riders: {name: string; value: number}[] = [],
  ) => {
    setHiCardLoading((p) => ({...p, [idx]: true}))
    try {
      const token = localStorage.getItem('TOKEN')
      const payload: Record<string, any> = {
        toolkit: 'HI',
        sex: hiForm.sex,
        state: hiForm.state,
        paymentType: hiForm.paymentType,
        days,
        dailyBenefit,
        carriersFilter: [companyId],
        riders,
      }
      if (hiForm.dob) {
        const [y, m, d] = hiForm.dob.split('-')
        payload.year = Number(y); payload.month = Number(m); payload.day = Number(d)
      } else {
        payload.age = Number(hiForm.age)
      }
      const res = await fetch(`${BACKEND_URL}/api/integrations/term-quote`, {
        method: 'POST',
        headers: {'Content-Type': 'application/json', Authorization: `Bearer ${token}`},
        body: JSON.stringify(payload),
      })
      if (!res.ok) throw new Error(`Error ${res.status}`)
      const data = await res.json()
      const quotes = (data.quotes || []).filter((q: any) => q.hi_response)
      if (quotes.length > 0) setHiCardData((p) => ({...p, [idx]: quotes[0]}))
    } catch {
      toast.error('Error loading Hospital Indemnity quote')
    } finally {
      setHiCardLoading((p) => ({...p, [idx]: false}))
    }
  }

  const refControl = useRef<HTMLInputElement>(null)
  const navigate = useNavigate()
  const [showMore, setShowMore] = useState(false)
  const [acaStateNode, setAcaStateNode] = useState<NodeStateEU | null>(null)

  const {data: dataStates} = useQuery<{allStatesList: NodeStateEU[]}>(GET_ALL_STATES_EU_LIST, {
    fetchPolicy: 'cache-first',
  })

  const nextMonthEffectiveDate = () => {
    const today = new Date()
    const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1)
    return nextMonth.toISOString().split('T')[0] // Devuelve en formato 'YYYY-MM-DD'
  }

  const defaultEffectiveDate = nextMonthEffectiveDate()

  const [formData, setFormData] = useState({
    queryType: 'BUSCAR_PLANES',
    effectiveDate: defaultEffectiveDate,
    year: getYearFromEffectiveDate(defaultEffectiveDate),
    householdIncome: '0',
    market: 'Individual',
    countyfips: '', //17033
    countyfipsName: '',
    state: '', //IL
    zipCode: '', //62466
    limit: SIZE_PAGE_QUICK_QUOTE,
    offset: 0,
    order: 'asc',
    utilizationLevel: 'Low',
    numberOfMembers: '1',
    personOne: {
      age: '',
      dob: '',
      gender: 'Male',
      isPregnant: false,
      pregnantWith: '',
      isParent: false,
      usesTobacco: false,
      lastTobaccoUseDate: '',
      hasMec: false,
      aptcEligible: true,
      utilizationLevel: '',
      relationship: '',
      doesNotCohabitate: '',
      currentEnrollment: null,
    },
    people: [], // Add the required 'people' property
  } as IForm)
  const [arrPeople, setArrPeople] = useState<IPerson[]>([])

  // const [eventKey, setEventKey] = useState('0')
  // const [showMore, setShowMore] = useState({personOne: false, people: {}})

  const [infoEnrollement] = useState({
    id: null as null | number,
    effectiveDate: null,
    planId: null as null | string,
    usesTobacco: false,
  })

  useEffect(() => {
    if (!data) return
    if (data.zipcodeByZip) {
      setFormData((p) => ({
        ...p,
        countyfips: data.zipcodeByZip.countyFips,
        state: data.zipcodeByZip.stateId,
        countyfipsName:
          data.zipcodeByZip.countyNamesAll +
          '(' +
          data.zipcodeByZip.countyFipsAll +
          ')/' +
          data.zipcodeByZip.stateName +
          '-' +
          data.zipcodeByZip.stateId +
          '/' +
          data.zipcodeByZip.zipCode,
      }))
    }
  }, [data])

  useEffect(() => {
    if (!dataMedSuppZip?.zipcodeByZip) return
    setMedSuppForm((p) => ({
      ...p,
      state: dataMedSuppZip.zipcodeByZip.stateId,
      countyName: dataMedSuppZip.zipcodeByZip.countyNamesAll.split('|')[0],
    }))
  }, [dataMedSuppZip])

  useEffect(() => {
    if (!dataDvhZip?.zipcodeByZip) return
    setDvhForm((p) => ({
      ...p,
      state: dataDvhZip.zipcodeByZip.stateId,
      countyName: dataDvhZip.zipcodeByZip.countyNamesAll.split('|')[0],
    }))
  }, [dataDvhZip])

  useEffect(() => {
    if (error) toast.error(`Error: ${error.message}`)
  }, [error])

  useEffect(() => {
    if (info?.dateBirthLead) {
      setFormData((p) => ({...p, dob: info.dateBirthLead || ''}))
    }
  }, [info])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {name, value, type, checked} = e.target
    if (name === 'effectiveDate') {
      setFormData((prevState) => ({
        ...prevState,
        effectiveDate: value,
        year: getYearFromEffectiveDate(value),
      }))
      return
    }
    setFormData((prevState) => ({
      ...prevState,
      [name]: type === 'checkbox' ? checked : value,
    }))
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    const {data: usLocationData} = await gqlCheckUsLocation()
    console.log('[ACA Search] isUsLocation check result:', usLocationData?.isUsLocation)
    if (usLocationData && usLocationData.isUsLocation === false) {
      setShowOutsideUsModal(true)
      return
    }

    const stateNode = dataStates?.allStatesList?.find(
      (s) => s.code?.toUpperCase() === formData.state?.toUpperCase()
    )
    if (stateNode?.jurisdictionType === 'STATE' && stateNode?.urlAcaQuote) {
      setAcaStateNode(stateNode)
      return
    }

    const obj = {
      ...formData,
      personOne: {
        ...formData.personOne,
        currentEnrollment:
          infoEnrollement.id === 1
            ? {
                id: 1,
                effectiveDate: infoEnrollement.effectiveDate,
                planId: infoEnrollement.planId,
                usesTobacco: infoEnrollement.usesTobacco,
              }
            : null,
      },
      householdIncome: formData.householdIncome.replaceAll(',', ''),
      people: [...arrPeople].map((inf) => {
        if (inf.id === infoEnrollement.id) {
          return {
            ...inf,
            currentEnrollment: {
              id: inf.id,
              effectiveDate: infoEnrollement.effectiveDate,
              planId: infoEnrollement.planId,
              usesTobacco: infoEnrollement.usesTobacco,
            },
          }
        }
        return {...inf}
      }),
    }
    const objStr = JSON.stringify(obj)
    // console.log('obj', obj)
    const decode = window.btoa(objStr)
    navigate(`/quote/${encodeURIComponent(decode)}`)
    onClose()
  }

  const handleAddPeople = () => {
    // setEventKey(`${arrPeople.length + 1}`)
    setArrPeople((p) => [
      ...p,
      {
        id: Date.now(),
        ...INIT_PEOPLE,
      },
    ])
  }

  const handleZipCodeLookup = () => {
    // console.log('blur or enter')

    if (formData.zipCode.length < 3) return

    setFormData((p) => ({
      ...p,
      countyfips: '',
      state: '',
    }))

    gqlGetMarket({
      variables: {
        zipCode: formData.zipCode,
      },
    })
  }

  // console.log('infoEnrollement.usesTobacco', infoEnrollement.usesTobacco)

  return (
    <>
    <Modal show={isOpen} onHide={onClose} className='modal-add-quote' dialogClassName='modal-90w'>
      <Modal.Header closeButton>
        <div className='d-flex align-items-center gap-4 w-100'>
          <Modal.Title>Quick Quote</Modal.Title>
          <div className='policy-form-tabs'>
            {([
              {key: 'aca', label: 'ACA'},
              {key: 'life', label: 'Life'},
              {key: 'medsupp', label: 'Medicare Supplement'},
              {key: 'dvh', label: 'DVH'},
              {key: 'hi', label: 'Hospital Indemnity'},
            ] as const).map(({key, label}) => (
              <div key={key} className={activeTab === key ? 'tab active' : 'tab'} onClick={() => setActiveTab(key)}>
                <button className='btn btn-tabs-text' style={{fontWeight: 600, color: activeTab === key ? '#5d78ff' : '#6c757d', fontSize: activeTab === key ? '15px' : '14px'}}>
                  {label}
                </button>
              </div>
            ))}
          </div>
        </div>
      </Modal.Header>

      {activeTab === 'life' ? (
        <>
          <div className='border-bottom px-4 pt-2'>
            <div className='policy-form-tabs'>
              {([
                {key: 'term', label: 'Term Life'},
                {key: 'fex', label: 'Final Expense'},
                {key: 'iul', label: 'Indexed Universal Life'},
              ] as const).map(({key, label}) => (
                <div key={key} className={lifeSubTab === key ? 'tab active' : 'tab'} onClick={() => setLifeSubTab(key)}>
                  <button className='btn btn-tabs-text' style={{fontWeight: 600, color: lifeSubTab === key ? '#5d78ff' : '#6c757d', fontSize: lifeSubTab === key ? '14px' : '13px'}}>
                    {label}
                  </button>
                </div>
              ))}
            </div>
          </div>
          {lifeSubTab === 'term' ? (
        lifeView === 'results' ? (
          <>
            <Modal.Body style={{maxHeight: '70vh', overflowY: 'auto'}}>
              <div className='d-flex align-items-center justify-content-between mb-4'>
                <Button
                  variant='link'
                  size='sm'
                  className='p-0 d-flex align-items-center gap-1 text-decoration-none'
                  onClick={() => setLifeView('form')}
                >
                  <i className='bi bi-arrow-left' />
                  Back to form
                </Button>
                <span className='text-muted small'>{lifeQuotes.length} result{lifeQuotes.length !== 1 ? 's' : ''}</span>
              </div>

              {lifeQuotes.length === 0 ? (
                <Alert variant='warning'>No quotes found for the selected criteria.</Alert>
              ) : (
                <div className='table-responsive'>
                  <table className='table table-hover align-middle'>
                    <thead className='table-dark'>
                      <tr>
                        <th>Company Name</th>
                        <th>{sharedLifeForm.quoteMode === 'face' ? 'Monthly' : 'Face Amount'}</th>
                        <th>Coverage Type</th>
                        <th>Actions</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {lifeQuotes.map((q, idx) => {
                        const isExp = lifeExpanded.has(idx)
                        const toggleExp = () => setLifeExpanded((prev) => {
                          const next = new Set(prev)
                          isExp ? next.delete(idx) : next.add(idx)
                          return next
                        })
                        return (
                          <>
                            <tr key={`term-row-${idx}`} style={{cursor: 'pointer'}} onClick={toggleExp}>
                              <td>
                                <div className='d-flex align-items-center gap-2'>
                                  {q.logo && <img src={q.logo} alt={q.company} style={{height: '48px', width: '100px', objectFit: 'contain'}} />}
                                  <div className='small'>
                                    <div className='fw-semibold'>{q.plan_name || q.company}</div>
                                    {q.plan_name && q.company !== q.plan_name && <div className='text-muted'>{q.company}</div>}
                                  </div>
                                </div>
                              </td>
                              <td className='fw-bold text-primary'>
                                ${sharedLifeForm.quoteMode === 'face' ? q.monthly : q.face_amount}
                                {sharedLifeForm.quoteMode === 'face' && <span className='fw-normal text-muted small'>/mo</span>}
                              </td>
                              <td className='small'>{q.plan_name}</td>
                              <td onClick={(e) => e.stopPropagation()}>
                                <div className='d-flex align-items-center gap-2'>
                                  {q.compensation_text && (
                                    <OverlayTrigger placement='top' overlay={<Tooltip>{q.compensation_text}</Tooltip>}>
                                      <i className='bi bi-currency-dollar text-success' style={{cursor: 'default', fontSize: '16px'}} />
                                    </OverlayTrigger>
                                  )}
                                  {q.warning && <i className='bi bi-exclamation-triangle text-warning' style={{fontSize: '15px'}} />}
                                  {q.eapp_link && q.eapp_link !== '#' && (
                                    <a href={q.eapp_link} target='_blank' rel='noopener noreferrer' className='btn btn-sm btn-outline-primary'>Apply</a>
                                  )}
                                </div>
                              </td>
                              <td><i className={`bi bi-chevron-${isExp ? 'up' : 'down'} text-muted`} /></td>
                            </tr>
                            {isExp && (
                              <tr key={`term-exp-${idx}`} style={{backgroundColor: '#f8f9fa'}}>
                                <td colSpan={5} className='small text-muted py-2 ps-4'>
                                  <div><strong>Annual Rate:</strong> ${q.yearly}</div>
                                  {q.face_amount && sharedLifeForm.quoteMode === 'face' && <div><strong>Coverage:</strong> ${q.face_amount}</div>}
                                  {q.monthly && sharedLifeForm.quoteMode === 'premium' && <div><strong>Monthly:</strong> ${q.monthly}/mo</div>}
                                  {q.addnd && <div>+ Accidental Death Monthly: ${q.addnd}</div>}
                                  {q.addnd_yearly && <div>+ Accidental Death Annual: ${q.addnd_yearly}</div>}
                                  {q.plan_info?.length > 0 && (
                                    <div className='d-flex flex-wrap gap-1 mt-1'>
                                      {q.plan_info.map((f: string) => (
                                        <span key={f} className='badge bg-light text-dark border'>{f}</span>
                                      ))}
                                    </div>
                                  )}
                                  {q.warning && <div className='text-warning mt-1'><i className='bi bi-exclamation-triangle me-1' />{q.warning}</div>}
                                </td>
                              </tr>
                            )}
                          </>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </Modal.Body>
            <Modal.Footer>
              <Button variant='secondary' onClick={onClose}>Close</Button>
            </Modal.Footer>
          </>
        ) : (
        <Form onSubmit={handleLifeSubmit}>
          <Modal.Body>
            <Row className='g-3'>
              {/* Left panel — Coverage Options */}
              <Col md={6}>
                <div className='border rounded p-4 h-100'>
                  <h6 className='fw-bold mb-3'>Coverage Options</h6>

                  <Form.Group className='mb-3'>
                    <Form.Label className='text-muted small mb-1'>Amount</Form.Label>
                    <div className='d-flex align-items-center gap-2'>
                      <Form.Control
                        type='number'
                        min={0}
                        placeholder='Face amount'
                        value={sharedLifeForm.faceAmount}
                        readOnly={sharedLifeForm.quoteMode === 'premium'}
                        onClick={() => sharedLifeForm.quoteMode === 'premium' && setSharedLifeForm((p) => ({...p, quoteMode: 'face', premiumAmount: ''}))}
                        onChange={(e) => setSharedLifeForm((p) => ({...p, faceAmount: e.target.value}))}
                        required={sharedLifeForm.quoteMode === 'face'}
                        style={{maxWidth: '160px', backgroundColor: sharedLifeForm.quoteMode === 'premium' ? '#f5f5f5' : undefined, cursor: sharedLifeForm.quoteMode === 'premium' ? 'pointer' : undefined}}
                      />
                      <span className='text-muted'>or</span>
                      <Form.Control
                        type='number'
                        min={0}
                        placeholder='Premium'
                        value={sharedLifeForm.premiumAmount}
                        readOnly={sharedLifeForm.quoteMode === 'face'}
                        onClick={() => sharedLifeForm.quoteMode === 'face' && setSharedLifeForm((p) => ({...p, quoteMode: 'premium', faceAmount: ''}))}
                        onChange={(e) => setSharedLifeForm((p) => ({...p, premiumAmount: e.target.value}))}
                        required={sharedLifeForm.quoteMode === 'premium'}
                        style={{maxWidth: '160px', backgroundColor: sharedLifeForm.quoteMode === 'face' ? '#f5f5f5' : undefined, cursor: sharedLifeForm.quoteMode === 'face' ? 'pointer' : undefined}}
                      />
                    </div>
                    <Form.Text className='text-muted'>
                      {sharedLifeForm.quoteMode === 'face' ? 'Quoting by face amount' : 'Quoting by monthly premium'}
                    </Form.Text>
                  </Form.Group>

                  <Form.Check
                    type='switch'
                    id='returnOfPremium'
                    label='Return of Premium'
                    checked={lifeForm.returnOfPremium}
                    onChange={(e) => setLifeForm((p) => ({...p, returnOfPremium: e.target.checked}))}
                  />
                </div>
              </Col>

              {/* Right panel — About the Client */}
              <Col md={6}>
                <div className='border rounded p-4 h-100'>
                  <h6 className='fw-bold mb-3'>About the Client</h6>

                  <Form.Group className='mb-3'>
                    <Form.Label className='text-muted small mb-1'>Sex</Form.Label>
                    <div className='d-flex gap-2'>
                      {['Male', 'Female'].map((s) => (
                        <Button
                          key={s}
                          type='button'
                          size='sm'
                          variant={sharedLifeForm.sex === s ? 'primary' : 'outline-secondary'}
                          style={{minWidth: '80px', borderRadius: '50px'}}
                          onClick={() => setSharedLifeForm((p) => ({...p, sex: s}))}
                        >
                          {s}
                        </Button>
                      ))}
                    </div>
                  </Form.Group>

                  <Form.Group className='mb-3'>
                    <Form.Label className='text-muted small mb-1'>State</Form.Label>
                    <Form.Select
                      value={sharedLifeForm.state}
                      onChange={(e) => setSharedLifeForm((p) => ({...p, state: e.target.value}))}
                      required
                    >
                      <option value=''>Select state</option>
                      {dataStates?.allStatesList
                        ?.filter((s) => s.code && s.code.length === 2)
                        .sort((a, b) => (a.code ?? '').localeCompare(b.code ?? ''))
                        .map((s) => (
                          <option key={s.code} value={s.code ?? ''}>
                            {s.code} – {s.name}
                          </option>
                        ))}
                    </Form.Select>
                  </Form.Group>

                  <Form.Group className='mb-3'>
                    <Form.Label className='text-muted small mb-1'>Birthday</Form.Label>
                    <div className='d-flex align-items-center gap-2'>
                      <DateInput
                        id='lifeDob'
                        name='dob'
                        value={sharedLifeForm.dob}
                        onChange={(_, value) => {
                          const age = value ? calculateAge(value) : null
                          setSharedLifeForm((p) => ({
                            ...p,
                            dob: value,
                            age: age ? String(age) : p.age,
                          }))
                        }}
                      />
                      <span className='text-muted'>or</span>
                      <Form.Control
                        type='number'
                        placeholder='age'
                        min={18}
                        max={80}
                        value={sharedLifeForm.age}
                        onChange={(e) => setSharedLifeForm((p) => ({...p, age: e.target.value, dob: ''}))}
                        style={{width: '72px'}}
                        required={!sharedLifeForm.dob}
                      />
                    </div>
                  </Form.Group>

                  <Form.Group>
                    <Form.Label className='text-muted small mb-1'>Nicotine Use</Form.Label>
                    <Form.Select
                      value={sharedLifeForm.tobacco}
                      onChange={(e) => setSharedLifeForm((p) => ({...p, tobacco: e.target.value}))}
                    >
                      {NICOTINE_OPTIONS.map((opt) => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </div>
              </Col>

              {/* Term Length */}
              <Col xs={12}>
                <Form.Label className='text-muted small mb-2 d-block text-center'>Term Length</Form.Label>
                <div className='d-flex justify-content-center'>
                  <div className='d-flex gap-1 border rounded-pill px-2 py-1'>
                    {TERM_OPTIONS.map((t) => (
                      <Button
                        key={t}
                        type='button'
                        size='sm'
                        variant={lifeForm.term === String(t) ? 'primary' : 'link'}
                        className='text-decoration-none'
                        style={{minWidth: '52px', borderRadius: '50px'}}
                        onClick={() => setLifeForm((p) => ({...p, term: String(t)}))}
                      >
                        {t}
                      </Button>
                    ))}
                  </div>
                </div>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant='secondary' type='button' onClick={onClose}>
              Close
            </Button>
            <Button variant='primary' type='submit' disabled={lifeLoading}>
              {lifeLoading ? (
                <><Spinner animation='border' size='sm' className='me-2' />Searching...</>
              ) : (
                <><i className='bi bi-search me-2' />Get Quote</>
              )}
            </Button>
          </Modal.Footer>
        </Form>
          )
          ) : lifeSubTab === 'fex' ? (
            fexView === 'results' ? (
              <>
                <Modal.Body style={{maxHeight: '70vh', overflowY: 'auto'}}>
                  <div className='d-flex align-items-center justify-content-between mb-4'>
                    <Button variant='link' size='sm' className='p-0 d-flex align-items-center gap-1 text-decoration-none' onClick={() => setFexView('form')}>
                      <i className='bi bi-arrow-left' /> Back to form
                    </Button>
                    <span className='text-muted small'>{fexQuotes.length} result{fexQuotes.length !== 1 ? 's' : ''}</span>
                  </div>
                  {fexQuotes.length === 0 ? (
                    <Alert variant='warning'>No quotes found for the selected criteria.</Alert>
                  ) : (
                    <div className='table-responsive'>
                      <table className='table table-hover align-middle'>
                        <thead className='table-dark'>
                          <tr>
                            <th>Company Name</th>
                            <th>{sharedLifeForm.quoteMode === 'face' ? 'Monthly' : 'Face Amount'}</th>
                            <th>Coverage Type</th>
                            <th>Actions</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {fexQuotes.map((q, idx) => {
                            const isExp = fexExpanded.has(idx)
                            const toggleExp = () => setFexExpanded((prev) => {
                              const next = new Set(prev)
                              isExp ? next.delete(idx) : next.add(idx)
                              return next
                            })
                            return (
                              <>
                                <tr key={`fex-row-${idx}`} style={{cursor: 'pointer'}} onClick={toggleExp}>
                                  <td>
                                    <div className='d-flex align-items-center gap-2'>
                                      {q.is_ssb_company && <i className='bi bi-star-fill text-warning' style={{fontSize: '12px'}} />}
                                      {q.logo && <img src={q.logo} alt={q.company} style={{height: '48px', width: '100px', objectFit: 'contain'}} />}
                                    </div>
                                  </td>
                                  <td className='fw-bold text-primary'>${sharedLifeForm.quoteMode === 'face' ? q.monthly : q.face_amount}</td>
                                  <td className='small'>{q.plan_name}</td>
                                  <td onClick={(e) => e.stopPropagation()}>
                                    <div className='d-flex align-items-center gap-2'>
                                      {q.compensation_text && (
                                        <OverlayTrigger placement='top' overlay={<Tooltip>{q.compensation_text}</Tooltip>}>
                                          <i className='bi bi-currency-dollar text-success' style={{cursor: 'default', fontSize: '16px'}} />
                                        </OverlayTrigger>
                                      )}
                                      {q.warning && <i className='bi bi-exclamation-triangle text-warning' style={{fontSize: '15px'}} />}
                                      {q.eapp_link && q.eapp_link !== '#' && (
                                        <a href={q.eapp_link} target='_blank' rel='noopener noreferrer' className='btn btn-sm btn-outline-primary'>Apply</a>
                                      )}
                                    </div>
                                  </td>
                                  <td><i className={`bi bi-chevron-${isExp ? 'up' : 'down'} text-muted`} /></td>
                                </tr>
                                {isExp && (
                                  <tr key={`fex-exp-${idx}`} style={{backgroundColor: '#f8f9fa'}}>
                                    <td colSpan={5} className='small text-muted py-2 ps-4'>
                                      <div><strong>Annual Rate:</strong> ${q.yearly}</div>
                                      {q.addnd && <div>+ Accidental Death Monthly Rate: ${q.addnd}</div>}
                                      {q.addnd_yearly && <div>+ Accidental Death Annual Rate: ${q.addnd_yearly}</div>}
                                      {q.warning && <div className='text-warning mt-1'><i className='bi bi-exclamation-triangle me-1' />{q.warning}</div>}
                                    </td>
                                  </tr>
                                )}
                              </>
                            )
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </Modal.Body>
                <Modal.Footer>
                  <Button variant='secondary' onClick={onClose}>Close</Button>
                </Modal.Footer>
              </>
            ) : (
              <Form onSubmit={handleFexSubmit}>
                <Modal.Body>
                  <Row className='g-3'>
                    <Col md={6}>
                      <div className='border rounded p-4 h-100'>
                        <h6 className='fw-bold mb-3'>Coverage Options</h6>
                        <Form.Group className='mb-3'>
                          <Form.Label className='text-muted small mb-1'>Amount</Form.Label>
                          <div className='d-flex align-items-center gap-2'>
                            <Form.Control
                              type='number'
                              min={0}
                              placeholder='Face amount'
                              value={sharedLifeForm.faceAmount}
                              readOnly={sharedLifeForm.quoteMode === 'premium'}
                              onClick={() => sharedLifeForm.quoteMode === 'premium' && setSharedLifeForm((p) => ({...p, quoteMode: 'face', premiumAmount: ''}))}
                              onChange={(e) => setSharedLifeForm((p) => ({...p, faceAmount: e.target.value}))}
                              required={sharedLifeForm.quoteMode === 'face'}
                              style={{maxWidth: '160px', backgroundColor: sharedLifeForm.quoteMode === 'premium' ? '#f5f5f5' : undefined, cursor: sharedLifeForm.quoteMode === 'premium' ? 'pointer' : undefined}}
                            />
                            <span className='text-muted'>or</span>
                            <Form.Control
                              type='number'
                              min={0}
                              placeholder='Premium'
                              value={sharedLifeForm.premiumAmount}
                              readOnly={sharedLifeForm.quoteMode === 'face'}
                              onClick={() => sharedLifeForm.quoteMode === 'face' && setSharedLifeForm((p) => ({...p, quoteMode: 'premium', faceAmount: ''}))}
                              onChange={(e) => setSharedLifeForm((p) => ({...p, premiumAmount: e.target.value}))}
                              required={sharedLifeForm.quoteMode === 'premium'}
                              style={{maxWidth: '160px', backgroundColor: sharedLifeForm.quoteMode === 'face' ? '#f5f5f5' : undefined, cursor: sharedLifeForm.quoteMode === 'face' ? 'pointer' : undefined}}
                            />
                          </div>
                          <Form.Text className='text-muted'>{sharedLifeForm.quoteMode === 'face' ? 'Quoting by face amount' : 'Quoting by monthly premium'}</Form.Text>
                        </Form.Group>

                        <Form.Group className='mb-3'>
                          <Form.Label className='text-muted small mb-1'>Coverage Type</Form.Label>
                          <Form.Select value={fexForm.coverageType} onChange={(e) => setFexForm((p) => ({...p, coverageType: e.target.value}))}>
                            {FEX_COVERAGE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                          </Form.Select>
                        </Form.Group>

                        <Form.Group>
                          <Form.Label className='text-muted small mb-1'>Payment Type</Form.Label>
                          <Form.Select value={fexForm.paymentType} onChange={(e) => setFexForm((p) => ({...p, paymentType: e.target.value}))}>
                            {FEX_PAYMENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                          </Form.Select>
                        </Form.Group>
                      </div>
                    </Col>

                    <Col md={6}>
                      <div className='border rounded p-4 h-100'>
                        <h6 className='fw-bold mb-3'>About the Client</h6>

                        <Form.Group className='mb-3'>
                          <Form.Label className='text-muted small mb-1'>Sex</Form.Label>
                          <div className='d-flex gap-2'>
                            {['Male', 'Female'].map((s) => (
                              <Button key={s} type='button' size='sm' variant={sharedLifeForm.sex === s ? 'primary' : 'outline-secondary'} style={{minWidth: '80px', borderRadius: '50px'}} onClick={() => setSharedLifeForm((p) => ({...p, sex: s}))}>
                                {s}
                              </Button>
                            ))}
                          </div>
                        </Form.Group>

                        <Form.Group className='mb-3'>
                          <Form.Label className='text-muted small mb-1'>State</Form.Label>
                          <Form.Select value={sharedLifeForm.state} onChange={(e) => setSharedLifeForm((p) => ({...p, state: e.target.value}))} required>
                            <option value=''>Select state</option>
                            {dataStates?.allStatesList?.filter((s) => s.code && s.code.length === 2).sort((a, b) => (a.code ?? '').localeCompare(b.code ?? '')).map((s) => (
                              <option key={s.code} value={s.code ?? ''}>{s.code} – {s.name}</option>
                            ))}
                          </Form.Select>
                        </Form.Group>

                        <Form.Group className='mb-3'>
                          <Form.Label className='text-muted small mb-1'>Birthday</Form.Label>
                          <div className='d-flex align-items-center gap-2'>
                            <DateInput
                              id='fexDob'
                              name='dob'
                              value={sharedLifeForm.dob}
                              onChange={(_, value) => {
                                const age = value ? calculateAge(value) : null
                                setSharedLifeForm((p) => ({...p, dob: value, age: age ? String(age) : p.age}))
                              }}
                            />
                            <span className='text-muted'>or</span>
                            <Form.Control type='number' placeholder='age' min={0} max={120} value={sharedLifeForm.age} onChange={(e) => setSharedLifeForm((p) => ({...p, age: e.target.value, dob: ''}))} style={{width: '72px'}} required={!sharedLifeForm.dob} />
                          </div>
                        </Form.Group>

                        <Form.Group>
                          <Form.Label className='text-muted small mb-1'>Nicotine Use</Form.Label>
                          <Form.Select value={sharedLifeForm.tobacco} onChange={(e) => setSharedLifeForm((p) => ({...p, tobacco: e.target.value}))}>
                            {NICOTINE_OPTIONS.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                          </Form.Select>
                        </Form.Group>
                      </div>
                    </Col>
                  </Row>
                </Modal.Body>
                <Modal.Footer>
                  <Button variant='secondary' type='button' onClick={onClose}>Close</Button>
                  <Button variant='primary' type='submit' disabled={fexLoading}>
                    {fexLoading ? <><Spinner animation='border' size='sm' className='me-2' />Searching...</> : <><i className='bi bi-search me-2' />Get Quote</>}
                  </Button>
                </Modal.Footer>
              </Form>
            )
          ) : lifeSubTab === 'iul' ? (
            iulView === 'results' ? (
              <>
                <Modal.Body style={{maxHeight: '70vh', overflowY: 'auto'}}>
                  <div className='d-flex align-items-center justify-content-between mb-4'>
                    <Button variant='link' size='sm' className='p-0 d-flex align-items-center gap-1 text-decoration-none' onClick={() => setIulView('form')}>
                      <i className='bi bi-arrow-left' /> Back to form
                    </Button>
                    <span className='text-muted small'>{iulQuotes.length} result{iulQuotes.length !== 1 ? 's' : ''}</span>
                  </div>
                  {iulQuotes.length === 0 ? (
                    <Alert variant='warning'>No quotes found for the selected criteria.</Alert>
                  ) : (
                    <div className='table-responsive'>
                      <table className='table table-hover align-middle'>
                        <thead className='table-dark'>
                          <tr>
                            <th>Company Name</th>
                            <th>{sharedLifeForm.quoteMode === 'face' ? 'Monthly' : 'Face Amount'}</th>
                            <th>Coverage Type</th>
                            <th>Actions</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {iulQuotes.map((q, idx) => {
                            const isExp = iulExpanded.has(idx)
                            const toggleExp = () => setIulExpanded((prev) => {
                              const next = new Set(prev)
                              isExp ? next.delete(idx) : next.add(idx)
                              return next
                            })
                            return (
                              <>
                                <tr key={`iul-row-${idx}`} style={{cursor: 'pointer'}} onClick={toggleExp}>
                                  <td>
                                    <div className='d-flex align-items-center gap-2'>
                                      {q.logo && <img src={q.logo} alt={q.company} style={{height: '48px', width: '100px', objectFit: 'contain'}} />}
                                      <div className='small'>
                                        <div className='fw-semibold'>{q.plan_name || q.company}</div>
                                        {q.plan_name && q.company !== q.plan_name && <div className='text-muted'>{q.company}</div>}
                                      </div>
                                    </div>
                                  </td>
                                  <td className='fw-bold text-primary'>
                                    ${sharedLifeForm.quoteMode === 'face' ? q.monthly : q.face_amount}
                                    {sharedLifeForm.quoteMode === 'face' && <span className='fw-normal text-muted small'>/mo</span>}
                                  </td>
                                  <td className='small'>{q.plan_name}</td>
                                  <td onClick={(e) => e.stopPropagation()}>
                                    <div className='d-flex align-items-center gap-2'>
                                      {q.compensation_text && (
                                        <OverlayTrigger placement='top' overlay={<Tooltip>{q.compensation_text}</Tooltip>}>
                                          <i className='bi bi-currency-dollar text-success' style={{cursor: 'default', fontSize: '16px'}} />
                                        </OverlayTrigger>
                                      )}
                                      {q.warning && <i className='bi bi-exclamation-triangle text-warning' style={{fontSize: '15px'}} />}
                                      {q.eapp_link && q.eapp_link !== '#' && (
                                        <a href={q.eapp_link} target='_blank' rel='noopener noreferrer' className='btn btn-sm btn-outline-primary'>Apply</a>
                                      )}
                                    </div>
                                  </td>
                                  <td><i className={`bi bi-chevron-${isExp ? 'up' : 'down'} text-muted`} /></td>
                                </tr>
                                {isExp && (
                                  <tr key={`iul-exp-${idx}`} style={{backgroundColor: '#f8f9fa'}}>
                                    <td colSpan={5} className='small text-muted py-2 ps-4'>
                                      <div><strong>Annual Rate:</strong> ${q.yearly}</div>
                                      {q.face_amount && sharedLifeForm.quoteMode === 'face' && <div><strong>Coverage:</strong> ${q.face_amount}</div>}
                                      {q.monthly && sharedLifeForm.quoteMode === 'premium' && <div><strong>Monthly:</strong> ${q.monthly}/mo</div>}
                                      {q.warning && <div className='text-warning mt-1'><i className='bi bi-exclamation-triangle me-1' />{q.warning}</div>}
                                    </td>
                                  </tr>
                                )}
                              </>
                            )
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </Modal.Body>
                <Modal.Footer>
                  <Button variant='secondary' onClick={onClose}>Close</Button>
                </Modal.Footer>
              </>
            ) : (
              <Form onSubmit={handleIulSubmit}>
                <Modal.Body>
                  <Row className='g-3'>
                    <Col md={6}>
                      <div className='border rounded p-4 h-100'>
                        <h6 className='fw-bold mb-3'>Coverage Options</h6>
                        <Form.Group className='mb-3'>
                          <Form.Label className='text-muted small mb-1'>Amount</Form.Label>
                          <div className='d-flex align-items-center gap-2'>
                            <Form.Control
                              type='number' min={0} placeholder='Face amount'
                              value={sharedLifeForm.faceAmount}
                              readOnly={sharedLifeForm.quoteMode === 'premium'}
                              onClick={() => sharedLifeForm.quoteMode === 'premium' && setSharedLifeForm((p) => ({...p, quoteMode: 'face', premiumAmount: ''}))}
                              onChange={(e) => setSharedLifeForm((p) => ({...p, faceAmount: e.target.value}))}
                              required={sharedLifeForm.quoteMode === 'face'}
                              style={{maxWidth: '160px', backgroundColor: sharedLifeForm.quoteMode === 'premium' ? '#f5f5f5' : undefined, cursor: sharedLifeForm.quoteMode === 'premium' ? 'pointer' : undefined}}
                            />
                            <span className='text-muted'>or</span>
                            <Form.Control
                              type='number' min={0} placeholder='Premium'
                              value={sharedLifeForm.premiumAmount}
                              readOnly={sharedLifeForm.quoteMode === 'face'}
                              onClick={() => sharedLifeForm.quoteMode === 'face' && setSharedLifeForm((p) => ({...p, quoteMode: 'premium', faceAmount: ''}))}
                              onChange={(e) => setSharedLifeForm((p) => ({...p, premiumAmount: e.target.value}))}
                              required={sharedLifeForm.quoteMode === 'premium'}
                              style={{maxWidth: '160px', backgroundColor: sharedLifeForm.quoteMode === 'face' ? '#f5f5f5' : undefined, cursor: sharedLifeForm.quoteMode === 'face' ? 'pointer' : undefined}}
                            />
                          </div>
                          <Form.Text className='text-muted'>{sharedLifeForm.quoteMode === 'face' ? 'Quoting by face amount' : 'Quoting by monthly premium'}</Form.Text>
                        </Form.Group>
                      </div>
                    </Col>

                    <Col md={6}>
                      <div className='border rounded p-4 h-100'>
                        <h6 className='fw-bold mb-3'>About the Client</h6>

                        <Form.Group className='mb-3'>
                          <Form.Label className='text-muted small mb-1'>Sex</Form.Label>
                          <div className='d-flex gap-2'>
                            {['Male', 'Female'].map((s) => (
                              <Button key={s} type='button' size='sm' variant={sharedLifeForm.sex === s ? 'primary' : 'outline-secondary'} style={{minWidth: '80px', borderRadius: '50px'}} onClick={() => setSharedLifeForm((p) => ({...p, sex: s}))}>
                                {s}
                              </Button>
                            ))}
                          </div>
                        </Form.Group>

                        <Form.Group className='mb-3'>
                          <Form.Label className='text-muted small mb-1'>State</Form.Label>
                          <Form.Select value={sharedLifeForm.state} onChange={(e) => setSharedLifeForm((p) => ({...p, state: e.target.value}))} required>
                            <option value=''>Select state</option>
                            {dataStates?.allStatesList?.filter((s) => s.code && s.code.length === 2).sort((a, b) => (a.code ?? '').localeCompare(b.code ?? '')).map((s) => (
                              <option key={s.code} value={s.code ?? ''}>{s.code} – {s.name}</option>
                            ))}
                          </Form.Select>
                        </Form.Group>

                        <Form.Group className='mb-3'>
                          <Form.Label className='text-muted small mb-1'>Birthday</Form.Label>
                          <div className='d-flex align-items-center gap-2'>
                            <DateInput
                              id='iulDob'
                              name='dob'
                              value={sharedLifeForm.dob}
                              onChange={(_, value) => {
                                const age = value ? calculateAge(value) : null
                                setSharedLifeForm((p) => ({...p, dob: value, age: age ? String(age) : p.age}))
                              }}
                            />
                            <span className='text-muted'>or</span>
                            <Form.Control type='number' placeholder='age' min={0} max={120} value={sharedLifeForm.age} onChange={(e) => setSharedLifeForm((p) => ({...p, age: e.target.value, dob: ''}))} style={{width: '72px'}} required={!sharedLifeForm.dob} />
                          </div>
                        </Form.Group>

                        <Form.Group>
                          <Form.Label className='text-muted small mb-1'>Nicotine Use</Form.Label>
                          <Form.Select value={sharedLifeForm.tobacco} onChange={(e) => setSharedLifeForm((p) => ({...p, tobacco: e.target.value}))}>
                            {NICOTINE_OPTIONS.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                          </Form.Select>
                        </Form.Group>
                      </div>
                    </Col>
                  </Row>
                </Modal.Body>
                <Modal.Footer>
                  <Button variant='secondary' type='button' onClick={onClose}>Close</Button>
                  <Button variant='primary' type='submit' disabled={iulLoading}>
                    {iulLoading ? <><Spinner animation='border' size='sm' className='me-2' />Searching...</> : <><i className='bi bi-search me-2' />Get Quote</>}
                  </Button>
                </Modal.Footer>
              </Form>
            )
          ) : null}
        </>
      ) : activeTab === 'medsupp' ? (
        <>
          {medSuppView === 'results' ? (
          <>
            <Modal.Body style={{maxHeight: '70vh', overflowY: 'auto'}}>
              <div className='d-flex align-items-center justify-content-between mb-4'>
                <Button variant='link' size='sm' className='p-0 d-flex align-items-center gap-1 text-decoration-none' onClick={() => setMedSuppView('form')}>
                  <i className='bi bi-arrow-left' /> Back to form
                </Button>
                <span className='text-muted small'>{medSuppQuotes.length} result{medSuppQuotes.length !== 1 ? 's' : ''}</span>
              </div>
              {medSuppQuotes.length === 0 ? (
                <Alert variant='warning'>No quotes found for the selected criteria.</Alert>
              ) : (
                <div className='table-responsive'>
                  <table className='table table-hover align-middle'>
                    <thead className='table-dark'>
                      <tr>
                        <th>Company</th>
                        <th>Monthly</th>
                        <th>Class Rating</th>
                        <th>Policy Fee</th>
                        <th>AM Best</th>
                        <th>HH Discount</th>
                        <th>Actions</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {medSuppQuotes.map((q, idx) => {
                        const isExpanded = medSuppExpanded.has(idx)
                        const toggleExpand = () => setMedSuppExpanded((prev) => {
                          const next = new Set(prev)
                          isExpanded ? next.delete(idx) : next.add(idx)
                          return next
                        })
                        const effectiveDateStr = q.effective_date
                          ? (() => { const d = new Date(q.effective_date); return `${String(d.getMonth()+1).padStart(2,'0')}/${String(d.getDate()).padStart(2,'0')}/${d.getFullYear()}` })()
                          : null
                        const hhd = q.med_supp_hhds?.[0]
                        const hhdInfo = q.plan_info?.[0]
                        return (
                          <>
                            <tr key={`row-${idx}`} style={{cursor: 'pointer'}} onClick={toggleExpand}>
                              <td>
                                <div className='d-flex align-items-center gap-2'>
                                  {q.logo && <img src={q.logo} alt={q.company} style={{height: '48px', width: '100px', objectFit: 'contain'}} />}
                                  <div className='fw-semibold small'>{q.company}</div>
                                </div>
                              </td>
                              <td>
                                <div className='fw-bold text-primary'>${q.monthly}</div>
                                {q.med_supp_hhds_applied_monthly?.[0] && (
                                  <div className='text-success small'>${q.med_supp_hhds_applied_monthly[0].discounted_rate?.toFixed(2)} w/disc</div>
                                )}
                              </td>
                              <td className='small'>{q.plan_name || '—'}</td>
                              <td className='small'>${q.med_supp_policy_fee || '0'}</td>
                              <td><span className='badge bg-light text-dark border'>{q.am_best_rating || '—'}</span></td>
                              <td className='small'>
                                <span>{hhd ? `${hhd}%` : '0%'}</span>
                                {hhdInfo && (
                                  <OverlayTrigger
                                    placement='left'
                                    overlay={
                                      <Tooltip id={`hhd-${idx}`}>
                                        <strong>Household Discount Guidelines</strong>
                                        <div className='mt-1'>{hhdInfo}</div>
                                      </Tooltip>
                                    }
                                  >
                                    <i className='bi bi-info-circle ms-1 text-muted' style={{cursor: 'default'}} onClick={(e) => e.stopPropagation()} />
                                  </OverlayTrigger>
                                )}
                              </td>
                              <td onClick={(e) => e.stopPropagation()}>
                                {q.eapp_link && q.eapp_link !== '#' && (
                                  <a href={q.eapp_link} target='_blank' rel='noopener noreferrer' className='btn btn-sm btn-outline-primary'>Apply</a>
                                )}
                              </td>
                              <td>
                                <i className={`bi bi-chevron-${isExpanded ? 'up' : 'down'} text-muted`} />
                              </td>
                            </tr>
                            {isExpanded && (
                              <tr key={`expand-${idx}`} style={{backgroundColor: '#f8f9fa'}}>
                                <td colSpan={8} className='small text-muted py-2 ps-4'>
                                  <div><strong>Annual Rate:</strong> ${q.yearly}</div>
                                  {q.med_supp_hhds_applied_monthly?.[0] && (
                                    <div>
                                      <strong>HHD Applied Monthly at {q.med_supp_hhds_applied_monthly[0].discount_percentage}%:</strong>{' '}
                                      ${q.med_supp_hhds_applied_monthly[0].discounted_rate?.toFixed(2)}
                                    </div>
                                  )}
                                  {q.med_supp_hhds_applied_yearly?.[0] && (
                                    <div>
                                      <strong>HHD Applied Yearly at {q.med_supp_hhds_applied_yearly[0].discount_percentage}%:</strong>{' '}
                                      ${q.med_supp_hhds_applied_yearly[0].discounted_rate?.toFixed(2)}
                                    </div>
                                  )}
                                  {effectiveDateStr && <div><strong>Effective Date:</strong> {effectiveDateStr}</div>}
                                </td>
                              </tr>
                            )}
                          </>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </Modal.Body>
            <Modal.Footer>
              <Button variant='secondary' onClick={onClose}>Close</Button>
            </Modal.Footer>
          </>
        ) : (
          <Form onSubmit={handleMedSuppSubmit}>
            <Modal.Body>
              <Row className='g-3'>
                {/* Left panel */}
                <Col md={6}>
                  <div className='border rounded p-4 h-100'>
                    <h6 className='fw-bold mb-3'>Plan Options</h6>

                    <Form.Group className='mb-3'>
                      <Form.Label className='text-muted small mb-1'>Plan Code</Form.Label>
                      <Form.Select value={medSuppForm.planCode} onChange={(e) => setMedSuppForm((p) => ({...p, planCode: e.target.value}))}>
                        {MED_SUPP_PLAN_CODES.map((code) => (
                          <option key={code} value={code}>{code}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>

                    <Form.Group className='mb-3'>
                      <Form.Label className='text-muted small mb-1'>Payment Type</Form.Label>
                      <Form.Select value={medSuppForm.paymentType} onChange={(e) => setMedSuppForm((p) => ({...p, paymentType: e.target.value}))}>
                        {PAYMENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                      </Form.Select>
                    </Form.Group>

                    <Form.Group>
                      <Form.Label className='text-muted small mb-1'>Effective Date</Form.Label>
                      <div className='d-flex gap-2 align-items-center'>
                        <DateInput
                          id='medSuppEffectiveDate'
                          name='effectiveDate'
                          value={medSuppForm.effectiveDate ?? ''}
                          onChange={(_, value) => setMedSuppForm((p) => ({...p, effectiveDate: value}))}
                        />
                        {(() => {
                          const effMonth = medSuppForm.effectiveDate
                            ? new Date(medSuppForm.effectiveDate).getMonth() + 1
                            : null
                          if (effMonth === 11 || effMonth === 12) {
                            const effYear = medSuppForm.effectiveDate
                              ? new Date(medSuppForm.effectiveDate).getFullYear()
                              : new Date().getFullYear()
                            const nextYear = effYear + 1
                            return (
                              <Button size='sm' variant='info' type='button' onClick={() => setMedSuppForm((p) => ({...p, effectiveDate: `${nextYear}-01-01`}))}>
                                Plans {nextYear}
                              </Button>
                            )
                          }
                          return null
                        })()}
                      </div>
                    </Form.Group>
                  </div>
                </Col>

                {/* Right panel */}
                <Col md={6}>
                  <div className='border rounded p-4 h-100'>
                    <h6 className='fw-bold mb-3'>About the Client</h6>

                    <Form.Group className='mb-3'>
                      <Form.Label className='text-muted small mb-1'>Sex</Form.Label>
                      <div className='d-flex gap-2'>
                        {['Male', 'Female'].map((s) => (
                          <Button key={s} type='button' size='sm' variant={sharedLifeForm.sex === s ? 'primary' : 'outline-secondary'} style={{minWidth: '80px', borderRadius: '50px'}} onClick={() => setSharedLifeForm((p) => ({...p, sex: s}))}>
                            {s}
                          </Button>
                        ))}
                      </div>
                    </Form.Group>

                    <Form.Group className='mb-3'>
                      <Form.Label className='text-muted small mb-1'>
                        Zipcode{loadingMedSuppZip && <Spinner animation='border' size='sm' className='ms-2' />}
                      </Form.Label>
                      <Form.Control
                        type='text'
                        placeholder='e.g. 33190'
                        value={medSuppForm.zipcode}
                        onChange={(e) => setMedSuppForm((p) => ({...p, zipcode: e.target.value}))}
                        onBlur={handleMedSuppZipLookup}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleMedSuppZipLookup() } }}
                        required
                      />
                      {dataMedSuppZip?.zipcodeByZip && (
                        <Form.Text className='text-muted'>
                          {medSuppForm.countyName}, {medSuppForm.state} — {dataMedSuppZip.zipcodeByZip.zipCode}
                        </Form.Text>
                      )}
                    </Form.Group>

                    <Form.Group className='mb-3'>
                      <Form.Label className='text-muted small mb-1'>Birthday</Form.Label>
                      <div className='d-flex align-items-center gap-2'>
                        <DateInput
                          id='medSuppDob'
                          name='dob'
                          value={sharedLifeForm.dob}
                          onChange={(_, value) => {
                            const age = value ? calculateAge(value) : null
                            setSharedLifeForm((p) => ({...p, dob: value, age: age ? String(age) : p.age}))
                          }}
                        />
                        <span className='text-muted'>or</span>
                        <Form.Control
                          type='number'
                          placeholder='age'
                          min={0}
                          max={120}
                          value={sharedLifeForm.age}
                          onChange={(e) => setSharedLifeForm((p) => ({...p, age: e.target.value, dob: ''}))}
                          style={{width: '72px'}}
                          required={!sharedLifeForm.dob}
                        />
                      </div>
                    </Form.Group>

                    <Form.Group>
                      <Form.Label className='text-muted small mb-1'>Nicotine Use</Form.Label>
                      <Form.Select value={sharedLifeForm.tobacco} onChange={(e) => setSharedLifeForm((p) => ({...p, tobacco: e.target.value}))}>
                        {NICOTINE_OPTIONS.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                      </Form.Select>
                    </Form.Group>
                  </div>
                </Col>
              </Row>
            </Modal.Body>
            <Modal.Footer>
              <Button variant='secondary' type='button' onClick={onClose}>Close</Button>
              <Button variant='primary' type='submit' disabled={medSuppLoading}>
                {medSuppLoading ? <><Spinner animation='border' size='sm' className='me-2' />Searching...</> : <><i className='bi bi-search me-2' />Get Quote</>}
              </Button>
            </Modal.Footer>
          </Form>
            )
          }
        </>
      ) : activeTab === 'dvh' ? (
        <>
          {dvhView === 'results' ? (
            <>
              <Modal.Body style={{maxHeight: '70vh', overflowY: 'auto'}}>
                <div className='d-flex align-items-center justify-content-between mb-4'>
                  <Button variant='link' size='sm' className='p-0 d-flex align-items-center gap-1 text-decoration-none' onClick={() => setDvhView('form')}>
                    <i className='bi bi-arrow-left' /> Back to form
                  </Button>
                  <span className='text-muted small'>{dvhQuotes.length} result{dvhQuotes.length !== 1 ? 's' : ''}</span>
                </div>
                {dvhQuotes.length === 0 ? (
                  <Alert variant='warning'>No quotes found for the selected criteria.</Alert>
                ) : (
                  <div>
                    {dvhQuotes.map((q: any, idx: number) => {
                      const cardQ = dvhCardData[idx] || q
                      const r = cardQ.ruleResult || {}
                      const planNames: string[] = r.plan_names || []
                      const coveredIndividualsList: string[] = r.covered_individuals || []
                      const deductiblesList: string[] = r.deductibles || []
                      const calBenefits: number[] = r.calendar_year_benefits || []

                      const selPlan = dvhCardPlan[idx] ?? planNames[0] ?? ''
                      const selIndividuals = dvhCardIndividuals[idx] ?? coveredIndividualsList[0] ?? 'Individual'
                      const selDeductible = dvhCardDeductible[idx] ?? deductiblesList[0] ?? ''
                      const selBenefit = dvhCardBenefit[idx] ?? (calBenefits[0] ? String(calBenefits[0]) : '')
                      const selRider = dvhCardRider[idx] ?? ''
                      const isCardLoading = dvhCardLoading[idx] ?? false

                      const baseMonthly = r.premium && r.premium_to_monthly_modal
                        ? r.premium * r.premium_to_monthly_modal
                        : r.premium ?? 0
                      const riderCost = selRider === 'select' ? (r.select_vision_rider || 0)
                        : selRider === 'choice' ? (r.choice_vision_rider || 0)
                        : selRider === 'vision' ? (r.vision_rider || 0) : 0
                      const monthly = typeof baseMonthly === 'number'
                        ? (baseMonthly + riderCost).toFixed(2) : '—'
                      const eappLink = (r.eapp_link || '').trim()

                      return (
                        <div key={idx} className='card mb-3 border shadow-sm'>
                          <div className='card-body p-3'>
                            <div className='d-flex gap-0 align-items-stretch' style={{minHeight: '120px'}}>

                              {/* Company + plan selector — 25% */}
                              <div className='d-flex flex-column align-items-center justify-content-center text-center pe-3 me-3 border-end' style={{width: '20%'}}>
                                {isCardLoading
                                  ? <div className='spinner-border spinner-border-sm text-primary' role='status' />
                                  : r.company_logo
                                    ? <img src={r.company_logo} alt={q.productTitle} style={{height: '48px', maxWidth: '90%', objectFit: 'contain'}} />
                                    : <span className='fw-bold small'>{q.productTitle}</span>
                                }
                                {planNames.length > 1 ? (
                                  <Form.Select
                                    size='sm'
                                    className='mt-2'
                                    value={selPlan}
                                    disabled={isCardLoading}
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={(e) => {
                                      const newPlan = e.target.value
                                      setDvhCardPlan((p) => ({...p, [idx]: newPlan}))
                                      fetchDvhCardQuote(idx, q.productName, newPlan, selIndividuals, selDeductible, selBenefit)
                                    }}
                                  >
                                    {planNames.map((name) => <option key={name} value={name}>{name}</option>)}
                                  </Form.Select>
                                ) : (
                                  <div className='small fw-semibold mt-2 text-muted'>{planNames[0] || q.productTitle}</div>
                                )}
                              </div>

                              {/* Price + covered_individuals — 25% */}
                              <div className='d-flex flex-column align-items-center justify-content-center text-center pe-3 me-3 border-end' style={{width: '20%'}}>
                                <div className='text-muted small mb-1'>Price</div>
                                <div className='fw-bold fs-5 text-primary'>${monthly}<span className='text-muted small fw-normal'>/mo</span></div>
                                {coveredIndividualsList.length > 1 ? (
                                  <Form.Select
                                    size='sm'
                                    className='mt-2'
                                    value={selIndividuals}
                                    disabled={isCardLoading}
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={(e) => {
                                      const newIndiv = e.target.value
                                      setDvhCardIndividuals((p) => ({...p, [idx]: newIndiv}))
                                      fetchDvhCardQuote(idx, q.productName, selPlan, newIndiv, selDeductible, selBenefit)
                                    }}
                                  >
                                    {coveredIndividualsList.map((ci) => <option key={ci} value={ci}>{ci}</option>)}
                                  </Form.Select>
                                ) : coveredIndividualsList.length === 1 ? (
                                  <div className='text-muted small mt-1'>{coveredIndividualsList[0]}</div>
                                ) : null}
                                {(r.select_vision_rider > 0 || r.choice_vision_rider > 0 || r.vision_rider > 0) && (
                                  <div className='mt-2 text-start w-100' style={{fontSize: '12px'}}>
                                    {r.vision_rider > 0 && (
                                      <Form.Check
                                        type='checkbox'
                                        label='Vision Rider'
                                        checked={selRider === 'vision'}
                                        onChange={() => setDvhCardRider((p) => ({...p, [idx]: selRider === 'vision' ? '' : 'vision'}))}
                                      />
                                    )}
                                    {r.select_vision_rider > 0 && (
                                      <Form.Check
                                        type='checkbox'
                                        label='Select Vision Rider'
                                        checked={selRider === 'select'}
                                        onChange={() => setDvhCardRider((p) => ({...p, [idx]: selRider === 'select' ? '' : 'select'}))}
                                      />
                                    )}
                                    {r.choice_vision_rider > 0 && (
                                      <Form.Check
                                        type='checkbox'
                                        label='Choice Vision Rider'
                                        checked={selRider === 'choice'}
                                        onChange={() => setDvhCardRider((p) => ({...p, [idx]: selRider === 'choice' ? '' : 'choice'}))}
                                      />
                                    )}
                                  </div>
                                )}
                              </div>

                              {/* Deductible — 25% */}
                              <div className='d-flex flex-column align-items-center justify-content-center text-center pe-3 me-3 border-end' style={{width: '20%', wordBreak: 'break-word'}}>
                                <div className='text-muted small mb-1'>Deductible</div>
                                {deductiblesList.length > 1 ? (
                                  <Form.Select
                                    size='sm'
                                    value={selDeductible}
                                    disabled={isCardLoading}
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={(e) => {
                                      const newDed = e.target.value
                                      setDvhCardDeductible((p) => ({...p, [idx]: newDed}))
                                      fetchDvhCardQuote(idx, q.productName, selPlan, selIndividuals, newDed, selBenefit)
                                    }}
                                  >
                                    {deductiblesList.map((d) => <option key={d} value={d}>{d}</option>)}
                                  </Form.Select>
                                ) : (
                                  <div className='small fw-semibold'>{deductiblesList[0] ?? '—'}</div>
                                )}
                                {r.deductible_info && <div className='text-muted mt-1' style={{fontSize: '11px'}}>{r.deductible_info}</div>}
                              </div>

                              {/* % Covered — 25% */}
                              <div className='d-flex flex-column justify-content-center pe-3 me-3 border-end' style={{width: '20%'}}>
                                <div className='text-muted small mb-2 fw-semibold'>% Covered by Plan</div>
                                <div className='d-flex justify-content-between small'>
                                  <span className='fw-semibold'>Preventive</span>
                                  <span>{r.preventive ?? '—'}{r.preventive ? '%' : ''}</span>
                                </div>
                                <div className='d-flex justify-content-between small'>
                                  <span className='fw-semibold'>Basic</span>
                                  <span>{r.basic ?? '—'}{r.basic && r.basic !== '0' ? '%' : ''}</span>
                                </div>
                                <div className='d-flex justify-content-between small'>
                                  <span className='fw-semibold'>Major</span>
                                  <span>{r.major ?? '—'}{r.major && r.major !== '0' ? '%' : ''}</span>
                                </div>
                              </div>

                              {/* Calendar Year Benefit — 25% */}
                              <div className='d-flex flex-column justify-content-center' style={{width: '20%'}}>
                                <div className='text-muted small fw-bold mb-2'>Calendar Year Benefit</div>
                                {calBenefits.length > 1 ? (
                                  <Form.Select
                                    size='sm'
                                    value={selBenefit}
                                    disabled={isCardLoading}
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={(e) => {
                                      const newBenefit = e.target.value
                                      setDvhCardBenefit((p) => ({...p, [idx]: newBenefit}))
                                      fetchDvhCardQuote(idx, q.productName, selPlan, selIndividuals, selDeductible, newBenefit)
                                    }}
                                  >
                                    {calBenefits.map((b) => <option key={b} value={String(b)}>${b}</option>)}
                                  </Form.Select>
                                ) : calBenefits.length === 1 ? (
                                  <div className='small fw-semibold'>${calBenefits[0]}</div>
                                ) : (
                                  <div className='small text-muted'>N/A</div>
                                )}
                                <div className='small mt-2'>
                                  Lifetime Max on Implants: <strong>{r.lifetime_max_benefits ?? 'N/A'}</strong>
                                </div>
                                <div className='small'>
                                  Orthodontic Benefits: <strong>{r.orthodontic_benefits ?? 'N/A'}</strong>
                                </div>
                              </div>

                            </div>

                            {/* Footer: Apply button */}
                            {eappLink && eappLink !== '#' && (
                              <div className='d-flex justify-content-end mt-3 pt-2 border-top'>
                                <a href={eappLink} target='_blank' rel='noopener noreferrer' className='btn btn-sm btn-outline-primary'>
                                  E-App
                                </a>
                              </div>
                            )}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}
              </Modal.Body>
              <Modal.Footer>
                <Button variant='secondary' onClick={onClose}>Close</Button>
              </Modal.Footer>
            </>
          ) : (
            <Form onSubmit={handleDvhSubmit}>
              <Modal.Body>
                <Row className='g-3'>
                  <Col md={6}>
                    <div className='border rounded p-4 h-100'>
                      <h6 className='fw-bold mb-3'>About the Client</h6>
                      <Form.Group>
                        <Form.Label className='text-muted small mb-1'>Birthday</Form.Label>
                        <div className='d-flex align-items-center gap-2'>
                          <DateInput
                            id='dvhDob'
                            name='dob'
                            value={dvhForm.dob}
                            onChange={(_, value) => {
                              const age = value ? calculateAge(value) : null
                              setDvhForm((p) => ({...p, dob: value, age: age ? String(age) : p.age}))
                            }}
                          />
                          <span className='text-muted'>or</span>
                          <Form.Control
                            type='number' placeholder='age' min={0} max={120}
                            value={dvhForm.age}
                            onChange={(e) => setDvhForm((p) => ({...p, age: e.target.value, dob: ''}))}
                            style={{width: '72px'}}
                            required={!dvhForm.dob}
                          />
                        </div>
                      </Form.Group>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className='border rounded p-4 h-100'>
                      <h6 className='fw-bold mb-3'>Location</h6>
                      <Form.Group>
                        <Form.Label className='text-muted small mb-1'>Zipcode</Form.Label>
                        <div className='d-flex align-items-center gap-2'>
                          <Form.Control
                            type='text' placeholder='33190' maxLength={5}
                            value={dvhForm.zipcode}
                            onChange={(e) => {
                              const z = e.target.value.replace(/\D/g, '').slice(0, 5)
                              setDvhForm((p) => ({...p, zipcode: z, countyName: '', state: ''}))
                              if (z.length === 5) gqlGetDvhZip({variables: {zipCode: z}})
                            }}
                            required
                            style={{maxWidth: '120px'}}
                          />
                          {loadingDvhZip && <Spinner animation='border' size='sm' />}
                        </div>
                        {dataDvhZip?.zipcodeByZip && (
                          <Form.Text className='text-muted'>
                            {dvhForm.countyName}, {dvhForm.state} — {dataDvhZip.zipcodeByZip.zipCode}
                          </Form.Text>
                        )}
                      </Form.Group>
                    </div>
                  </Col>
                </Row>
              </Modal.Body>
              <Modal.Footer>
                <Button variant='secondary' type='button' onClick={onClose}>Close</Button>
                <Button variant='primary' type='submit' disabled={dvhLoading}>
                  {dvhLoading ? <><Spinner animation='border' size='sm' className='me-2' />Searching...</> : <><i className='bi bi-search me-2' />Get Quote</>}
                </Button>
              </Modal.Footer>
            </Form>
          )}
        </>
      ) : activeTab === 'hi' ? (
        <>
          {hiView === 'results' ? (
            <>
              <Modal.Body style={{maxHeight: '70vh', overflowY: 'auto'}}>
                <div className='d-flex align-items-center justify-content-between mb-4'>
                  <Button variant='link' size='sm' className='p-0 d-flex align-items-center gap-1 text-decoration-none' onClick={() => setHiView('form')}>
                    <i className='bi bi-arrow-left' /> Back to form
                  </Button>
                  <span className='text-muted small'>{hiQuotes.length} result{hiQuotes.length !== 1 ? 's' : ''}</span>
                </div>
                {hiQuotes.length === 0 ? (
                  <Alert variant='warning'>No quotes found for the selected criteria.</Alert>
                ) : (
                  <div>
                    {hiQuotes.map((q: any, idx: number) => {
                      const cardQ = hiCardData[idx] || q
                      const r = cardQ.hi_response || {}
                      const daysOptions: number[] = r.days?.data || []
                      const dailyOptions: number[] = r.daily_benefits?.data || []
                      const selDays = hiCardDays[idx] ?? daysOptions[0] ?? 3
                      const selDaily = hiCardDaily[idx] ?? dailyOptions[0] ?? 100
                      const isCardLoading = hiCardLoading[idx] ?? false
                      // Use base_rate from API response (already computed for selDays/selDaily)
                      // Fallback to client-side calc for initial render before any API call
                      const dayKey = `${selDays}_days`
                      const baseRate: number = hiCardData[idx] ? (r.base_rate ?? 0) : ((r[dayKey] ?? r.base_rate ?? 0) * (selDaily / (r.base_per || 50)))
                      const monthly = baseRate.toFixed(2)
                      const discountMonthly = r.household_discount_applied != null
                        ? r.household_discount_applied.toFixed(2)
                        : r.household_discount?.data
                          ? (baseRate * (1 - r.household_discount.data / 100)).toFixed(2)
                          : null
                      const riderSelections = hiCardRiders[idx] || {}
                      const currentRiders = Object.entries(riderSelections)
                        .filter(([, v]) => v > 0)
                        .map(([name, value]) => ({name, value}))
                      const isExpanded = hiCardExpanded.has(idx)
                      const toggleExpand = () => setHiCardExpanded((prev) => {
                        const next = new Set(prev)
                        isExpanded ? next.delete(idx) : next.add(idx)
                        return next
                      })
                      const planNames: string[] = r.plan_names || []
                      const eappLink = (q.eapp_link || '').trim()

                      return (
                        <div key={idx} className='card mb-3 border shadow-sm'>
                          <div className='card-body p-3'>
                            <div className='d-flex gap-0 align-items-stretch' style={{minHeight: '120px'}}>

                              {/* Company — 25% */}
                              <div className='d-flex flex-column align-items-center justify-content-center text-center pe-3 me-3 border-end' style={{width: '25%'}}>
                                {isCardLoading
                                  ? <div className='spinner-border spinner-border-sm text-primary' role='status' />
                                  : r.logo
                                    ? <img src={r.logo} alt={q.company} style={{height: '64px', maxWidth: '90%', objectFit: 'contain'}} />
                                    : <span className='fw-bold small'>{q.company}</span>
                                }
                                <div className='small fw-semibold mt-2 text-muted'>{planNames[0] || ''}</div>
                              </div>

                              {/* Monthly Premium — 25% */}
                              <div className='d-flex flex-column justify-content-center align-items-center pe-3 me-3 border-end' style={{width: '25%'}}>
                                <div className='text-muted small mb-1 fw-semibold'>Monthly Premium</div>
                                <div className='fw-bold fs-3 text-primary'>${monthly}</div>
                                {discountMonthly && (
                                  <div className='d-flex justify-content-between small mt-1 w-100'>
                                    <span className='text-muted'>{r.household_discount.name}</span>
                                    <span className='fw-semibold'>${discountMonthly}</span>
                                  </div>
                                )}
                                {r.policy_fee?.data && (
                                  <div className='d-flex justify-content-between small w-100'>
                                    <span className='text-muted'>{r.policy_fee.name}</span>
                                    <span className='fw-semibold'>{r.policy_fee.data}</span>
                                  </div>
                                )}
                              </div>

                              {/* Days Payable — 25% */}
                              <div className='d-flex flex-column justify-content-center pe-3 me-3 border-end' style={{width: '25%'}}>
                                <div className='text-muted small mb-1 fw-semibold'>{r.days?.name || 'Number of Days Payable'}</div>
                                <Form.Select
                                  size='sm'
                                  value={selDays}
                                  disabled={isCardLoading}
                                  onChange={(e) => {
                                    const newDays = Number(e.target.value)
                                    setHiCardDays((p) => ({...p, [idx]: newDays}))
                                    fetchHiCardQuote(idx, q.company_id, newDays, selDaily, currentRiders)
                                  }}
                                >
                                  {daysOptions.map((d) => <option key={d} value={d}>{d}{r.days?.suffix || ' days'}</option>)}
                                </Form.Select>
                              </div>

                              {/* Daily Benefit — 25% */}
                              <div className='d-flex flex-column justify-content-center' style={{width: '25%'}}>
                                <div className='text-muted small mb-1 fw-semibold'>{r.daily_benefits?.name || 'Daily Benefit Amount'}</div>
                                <Form.Select
                                  size='sm'
                                  value={selDaily}
                                  disabled={isCardLoading}
                                  onChange={(e) => {
                                    const newDaily = Number(e.target.value)
                                    setHiCardDaily((p) => ({...p, [idx]: newDaily}))
                                    fetchHiCardQuote(idx, q.company_id, selDays, newDaily, currentRiders)
                                  }}
                                >
                                  {dailyOptions.map((d) => <option key={d} value={d}>{r.daily_benefits?.prefix || '$'}{d}</option>)}
                                </Form.Select>
                                {r.daily_benefits?.info && (
                                  <div className='text-muted mt-1' style={{fontSize: '11px'}}>{r.daily_benefits.info}</div>
                                )}
                              </div>

                            </div>

                            {/* Optional Riders toggle */}
                            {r.optional_riders?.length > 0 && (
                              <>
                                <div className='mt-3 pt-2 border-top'>
                                  <Button variant='link' size='sm' className='p-0 text-decoration-none' onClick={toggleExpand}>
                                    <i className={`bi bi-chevron-${isExpanded ? 'up' : 'down'} me-1`} />
                                    Optional Add-on Benefits
                                  </Button>
                                </div>
                                {isExpanded && (
                                  <div className='mt-2'>
                                    <Row className='g-2'>
                                      {r.optional_riders.map((rider: any, ri: number) => (
                                        <Col md={6} key={ri}>
                                          <Form.Group>
                                            <Form.Label className='small text-muted mb-1'>{rider.name}</Form.Label>
                                            <Form.Select
                                              size='sm'
                                              value={riderSelections[rider.name] || ''}
                                              disabled={isCardLoading}
                                              onChange={(e) => {
                                                const val = e.target.value ? Number(e.target.value) : 0
                                                const newSelections = {...riderSelections, [rider.name]: val}
                                                setHiCardRiders((p) => ({...p, [idx]: newSelections}))
                                                const newRidersArray = (Object.entries(newSelections) as [string, number][])
                                                  .filter(([, v]) => v > 0)
                                                  .map(([name, value]) => ({name, value}))
                                                fetchHiCardQuote(idx, q.company_id, selDays, selDaily, newRidersArray)
                                              }}
                                            >
                                              <option value=''>—</option>
                                              {rider.data.map((val: number) => (
                                                <option key={val} value={val}>{rider.prefix || ''}{val}{rider.suffix || ''}</option>
                                              ))}
                                            </Form.Select>
                                          </Form.Group>
                                        </Col>
                                      ))}
                                    </Row>
                                  </div>
                                )}
                              </>
                            )}

                            {/* Footer */}
                            {eappLink && eappLink !== '#' && (
                              <div className='d-flex justify-content-end mt-3 pt-2 border-top'>
                                <a href={eappLink} target='_blank' rel='noopener noreferrer' className='btn btn-sm btn-outline-primary'>E-App</a>
                              </div>
                            )}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}
              </Modal.Body>
              <Modal.Footer>
                <Button variant='secondary' onClick={onClose}>Close</Button>
              </Modal.Footer>
            </>
          ) : (
            <Form onSubmit={handleHiSubmit}>
              <Modal.Body>
                <Row className='g-3'>
                  <Col md={6}>
                    <div className='border rounded p-4 h-100'>
                      <h6 className='fw-bold mb-3'>About the Client</h6>
                      <Form.Group className='mb-3'>
                        <Form.Label className='text-muted small mb-1'>Birthday</Form.Label>
                        <div className='d-flex align-items-center gap-2'>
                          <DateInput
                            id='hiDob'
                            name='dob'
                            value={hiForm.dob}
                            onChange={(_, value) => {
                              const age = value ? calculateAge(value) : null
                              setHiForm((p) => ({...p, dob: value, age: age ? String(age) : p.age}))
                            }}
                          />
                          <span className='text-muted small'>or</span>
                          <Form.Control
                            type='number'
                            placeholder='Age'
                            style={{width: '80px'}}
                            value={hiForm.age}
                            min={0}
                            max={120}
                            required={!hiForm.dob}
                            onChange={(e) => setHiForm((p) => ({...p, age: e.target.value, dob: ''}))}
                          />
                        </div>
                      </Form.Group>
                      <Form.Group className='mb-3'>
                        <Form.Label className='text-muted small mb-1'>Sex</Form.Label>
                        <div className='d-flex gap-2'>
                          {['Male', 'Female'].map((s) => (
                            <Button
                              key={s}
                              size='sm'
                              variant={hiForm.sex === s ? 'dark' : 'outline-secondary'}
                              onClick={() => setHiForm((p) => ({...p, sex: s}))}
                              type='button'
                            >{s}</Button>
                          ))}
                        </div>
                      </Form.Group>
                      <Form.Group className='mb-3'>
                        <Form.Label className='text-muted small mb-1'>State</Form.Label>
                        <Form.Select
                          value={hiForm.state}
                          required
                          onChange={(e) => setHiForm((p) => ({...p, state: e.target.value}))}
                        >
                          <option value=''>Select state</option>
                          {dataStates?.allStatesList
                            ?.filter((s) => s.code && s.code.length === 2)
                            .sort((a, b) => (a.code ?? '').localeCompare(b.code ?? ''))
                            .map((s) => (
                              <option key={s.code} value={s.code ?? ''}>{s.code} – {s.name}</option>
                            ))}
                        </Form.Select>
                      </Form.Group>
                      <Form.Group>
                        <Form.Label className='text-muted small mb-1'>Payment Type</Form.Label>
                        <Form.Select
                          value={hiForm.paymentType}
                          onChange={(e) => setHiForm((p) => ({...p, paymentType: e.target.value}))}
                        >
                          {PAYMENT_TYPES.map((pt) => <option key={pt} value={pt}>{pt}</option>)}
                        </Form.Select>
                      </Form.Group>
                    </div>
                  </Col>
                </Row>
              </Modal.Body>
              <Modal.Footer>
                <Button variant='secondary' type='button' onClick={onClose}>Cancel</Button>
                <Button variant='primary' type='submit' disabled={hiLoading}>
                  {hiLoading ? <><span className='spinner-border spinner-border-sm me-2' />Loading...</> : 'Get Quote'}
                </Button>
              </Modal.Footer>
            </Form>
          )}
        </>
      ) : (
      <Form
        onSubmit={handleSubmit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.preventDefault()
        }}
      >
        <Modal.Body>
          <Alert variant='primary' className='my-5 d-flex align-items-center gap-2'>
            <span>
              <i className='bi bi-exclamation-circle fs-1' style={{color: 'inherit'}} />
            </span>
            Please fill in the necessary information below to receive a quote for insurance plans
          </Alert>
          <Row className='d-flex align-items-center justify-content-between'>
            <Col lg={2} md={6} sm={12} xs={12}>
              <Form.Group>
                <Form.Label>Household Income</Form.Label>
                <Form.Control
                  type='text'
                  name='householdIncome'
                  value={formData.householdIncome}
                  onChange={(e) => {
                    const {value} = e.target
                    const noComma = value.replaceAll(',', '')
                    if (noComma !== '' && isNaN(Number(noComma))) return
                    const str = Intl.NumberFormat('en-US').format(Number(noComma))
                    setFormData((p) => ({...p, householdIncome: str}))
                  }}
                  required
                />
              </Form.Group>
            </Col>
            <Col lg={2} md={2} sm={6} xs={6}>
              <Form.Group>
                <Form.Label title='People in their tax household'>Number of Members</Form.Label>
                <Form.Control
                  type='number'
                  name='numberOfMembers'
                  value={formData.numberOfMembers}
                  required
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      numberOfMembers: e.target.value,
                    }))
                  }
                />
              </Form.Group>
            </Col>
            <Col lg={2} md={6} sm={12} xs={12}>
              <Form.Group>
                <Form.Label>Zip Code</Form.Label>
                <Form.Control
                  ref={refControl}
                  type='text'
                  name='zipCode'
                  value={formData.zipCode}
                  onChange={handleChange}
                  onBlur={handleZipCodeLookup}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault() // Evitar submit
                      handleZipCodeLookup()
                    }
                  }}
                />
              </Form.Group>
            </Col>

            <Col lg={3} md={6} sm={12} xs={12}>
              <Form.Group>
                <Form.Label>County FIPS</Form.Label>{' '}
                {loadingZipCode && (
                  <span>
                    <Spinner animation='border' size='sm' />
                  </span>
                )}
                <Form.Select
                  disabled={loadingZipCode || formData.zipCode?.length < 3}
                  value={formData.countyfips}
                  onChange={(e) =>
                    setFormData((p) => ({
                      ...p,
                      countyfips: e.target.value,
                      countyfipsName: e.target.selectedOptions[0].text,
                    }))
                  }
                >
                  <option value=''>Select county FIPS</option>
                  {data &&
                    data?.zipcodeByZip?.countyNamesAll?.split('|').map((str, idx) => (
                      <option key={str} value={data?.zipcodeByZip?.countyFipsAll?.split('|')[idx]}>
                        {str} - {data?.zipcodeByZip?.countyFipsAll?.split('|')[idx]}
                      </option>
                    ))}
                </Form.Select>
              </Form.Group>
            </Col>

            <Col lg={3} md={6} sm={12} xs={12}>
              <Form.Group>
                <Form.Label>Effective date</Form.Label>
                <div className='d-flex gap-2 align-items-center'>
                  <DateInput
                    id='effectiveDate'
                    name='effectiveDate'
                    value={formData.effectiveDate ?? ''}
                    onChange={(_, value) =>
                      setFormData((p) => ({
                        ...p,
                        effectiveDate: value,
                        year: getYearFromEffectiveDate(value),
                      }))
                    }
                  />
                  {(() => {
                    const effectiveMonth = formData.effectiveDate
                      ? new Date(formData.effectiveDate).getMonth() + 1
                      : null
                    if (effectiveMonth === 11 || effectiveMonth === 12) {
                      const currentYear = new Date().getFullYear()
                      const effectiveYear = formData.effectiveDate
                        ? new Date(formData.effectiveDate).getFullYear()
                        : currentYear
                      const nextYear = effectiveYear + 1
                      return (
                        <Button
                          size='sm'
                          variant='info'
                          type='button'
                          onClick={() =>
                            setFormData((p) => ({
                              ...p,
                              effectiveDate: `${nextYear}-01-01`,
                              year: nextYear,
                            }))
                          }
                        >
                          Plans {nextYear}
                        </Button>
                      )
                    }
                    return null
                  })()}
                </div>
              </Form.Group>
            </Col>

            <Col style={{display: 'none'}} lg={3} md={6} sm={12} xs={12}>
              <Form.Group>
                <Form.Label>County FIPS</Form.Label>
                <Form.Control
                  type='text'
                  name='countyfips'
                  value={formData.countyfips}
                  required
                  // onChange={handleChange}
                  readOnly
                />
              </Form.Group>
            </Col>
            <Col style={{display: 'none'}} lg={3} md={6} sm={12} xs={12}>
              <Form.Group>
                <Form.Label>State</Form.Label>
                <Form.Control
                  type='text'
                  name='state'
                  value={formData.state}
                  required
                  // onChange={handleChange}
                  readOnly
                />
              </Form.Group>
            </Col>
            {/* <Col lg={3} md={6} sm={12} xs={12}>
              <Form.Group>
                <Form.Label>Order</Form.Label>
                <Form.Control
                  as='select'
                  name='order'
                  value={formData.order}
                  onChange={handleChange}
                >
                  <option value='asc'>Ascending</option>
                  <option value='desc'>Descending</option>
                </Form.Control>
              </Form.Group>
            </Col> */}
          </Row>
          <hr />
          <div>
            {/* Tabla para mostrar los miembros */}
            <div className='mt-4 table-scrollable table-container'>
              <div className='d-flex justify-content-between align-items-center'>
                <h5 className='mb-3'>Members</h5>
                <Button variant='link' size='sm' onClick={() => setShowMore((prev) => !prev)}>
                  {showMore ? 'Show less' : 'Show more'}
                </Button>
              </div>
              <table className='w-100 table-scrollable table-scroll table-light table-striped table-sm'>
                <thead>
                  <tr role='row'>
                    <th className='fw-bold'>#</th>
                    <th className='fw-bold'>Date of Birth</th>
                    <th className='fw-bold'>Age</th>
                    <th className='fw-bold'>Gender</th>
                    <th className='fw-bold'>Uses Tobacco</th>
                    <th className='fw-bold'>Is Pregnant</th>
                    <th className='fw-bold show-more'>APTC Eligible</th>
                    {showMore && (
                      <>
                        <th className='fw-bold show-more'>Is Parent</th>

                        <th className='fw-bold show-more'>Has MEC</th>
                      </>
                    )}
                    {/* <th className='fw-bold'>Has Married Couple</th> */}
                    <th className='fw-bold'>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {/* Mostrar el primer miembro (personOne) */}
                  <tr>
                    <td>1</td>
                    <td>
                      <DateInput
                        id='dobPersonOne'
                        name='dob'
                        value={formData.personOne?.dob || ''}
                        onChange={(_, value) => {
                          if (!value) {
                            setFormData((p) => ({
                              ...p,
                              personOne: {...p.personOne, dob: '', age: ''},
                            }))
                            return
                          }
                          const age = calculateAge(value)
                          setFormData((p) => ({
                            ...p,
                            personOne: {
                              ...p.personOne,
                              dob: value,
                              age: age ? `${age}` : '',
                              hasMec: age && Number(age) < 19 ? true : false,
                            },
                          }))
                        }}
                      />
                    </td>
                    <td>
                      <Form.Control
                        className='input-age'
                        required={Boolean(!formData.personOne?.age && !formData.personOne?.dob)}
                        type='number'
                        name='Age'
                        disabled={Boolean(formData.personOne?.dob)}
                        value={formData.personOne?.age}
                        onChange={(e) => {
                          const { value } = e.target
                          if (value === '') {
                            setFormData((p) => ({
                              ...p,
                              personOne: { ...p.personOne, age: '' },
                            }))
                            return
                          }
                          if (isNaN(Number(value))) return

                          setFormData((p) => ({
                            ...p,
                            personOne: {
                              ...p.personOne,
                              age: value,
                              hasMec: Number(value) < 19 ? true : false, // 👈 fuerza MEC si la edad < 19
                            },
                          }))
                        }}
                      />
                    </td>
                    <td>
                      <Form.Control
                        required
                        className='input-gender'
                        as='select'
                        name='gender'
                        value={formData.personOne?.gender}
                        onChange={(e) => {
                          setFormData((p) => ({
                            ...p,
                            personOne: {...p.personOne, gender: e.target.value},
                          }))
                        }}
                      >
                        {GENDER_OPTIONS.map(({value, label}) => (
                          <option key={value} value={value}>
                            {label}
                          </option>
                        ))}
                      </Form.Control>
                    </td>
                    <td>
                      <Form.Check
                        type='switch'
                        name='usesTobacco'
                        id='usesTobacco_1'
                        checked={formData.personOne?.usesTobacco}
                        onChange={(e) => {
                          setFormData((p) => ({
                            ...p,
                            personOne: {...p.personOne, usesTobacco: e.target.checked},
                          }))
                        }}
                      />
                    </td>
                    <td>
                      <Form.Check
                        type='switch'
                        name='isPregnant'
                        id='isPregnant'
                        checked={formData.personOne?.isPregnant}
                        onChange={(e) => {
                          setFormData((p) => ({
                            ...p,
                            personOne: {...p.personOne, isPregnant: e.target.checked},
                          }))
                        }}
                      />
                    </td>
                    <td>
                      <Form.Check
                        type='switch'
                        name='aptcEligible'
                        id='aptcEligible'
                        checked={formData.personOne?.aptcEligible}
                        onChange={(e) => {
                          setFormData((p) => ({
                            ...p,
                            personOne: {...p.personOne, aptcEligible: e.target.checked},
                          }))
                        }}
                      />
                    </td>
                    {showMore && (
                      <>
                        <td className='show-more'>
                          <Form.Check
                            type='switch'
                            name='isParent'
                            id='isParent'
                            checked={formData.personOne?.isParent}
                            onChange={(e) => {
                              setFormData((p) => ({
                                ...p,
                                personOne: {...p.personOne, isParent: e.target.checked},
                              }))
                            }}
                          />
                        </td>

                        <td className='show-more'>
                          <Form.Check
                            type='switch'
                            name='hasMec'
                            id='hasMec'
                            checked={formData.personOne?.hasMec}
                            onChange={(e) => {
                              setFormData((p) => ({
                                ...p,
                                personOne: {...p.personOne, hasMec: e.target.checked},
                              }))
                            }}
                          />
                        </td>
                      </>
                    )}
                    <td> </td>
                  </tr>
                  {/* Mostrar los demás miembros (arrPeople) */}
                  {arrPeople?.map((inf, idx) => (
                    <tr key={inf.id}>
                      <td>{idx + 2}</td>
                      <td>
                        <DateInput
                          id={`dob_${inf.id}`}
                          name='dob'
                          value={inf.dob || ''}
                          onChange={(_, value) => {
                            const ageC = calculateAge(value)
                            setArrPeople((prevPeople) =>
                              prevPeople.map((person) =>
                                person.id === inf.id
                                  ? {
                                      ...person,
                                      dob: value,
                                      age: ageC ? `${ageC}` : '',
                                      hasMec: ageC && ageC < 19 ? true : false,
                                    }
                                  : person
                              )
                            )
                          }}
                        />
                      </td>
                      <td>
                        <Form.Control
                          type='number'
                          name='Age'
                          className='input-age'
                          required={Boolean(!inf.age && !inf.dob)}
                          disabled={Boolean(inf.dob)}
                          value={inf.age}
                          onChange={(e) => {
                            const {value} = e.target

                            if (value === '') {
                              setArrPeople((prevPeople) =>
                                prevPeople.map((person) =>
                                  person.id === inf.id ? {...person, age: ''} : person
                                )
                              )
                            }
                            if (isNaN(Number(value))) return

                            setArrPeople((prevPeople) =>
                              prevPeople.map((person) =>
                                person.id === inf.id
                                  ? {
                                      ...person,
                                      age: value,
                                      hasMec: Number(value) < 19 ? true : false,
                                    }
                                  : person
                              )
                            )
                          }}
                        />
                      </td>
                      <td>
                        <Form.Control
                          required
                          as='select'
                          name='gender'
                          value={inf.gender}
                          onChange={(e) => {
                            const {value} = e.target
                            setArrPeople((prevPeople) =>
                              prevPeople.map((person) =>
                                person.id === inf.id ? {...person, gender: value} : person
                              )
                            )
                          }}
                        >
                          {GENDER_OPTIONS.map(({value, label}) => (
                            <option key={value} value={value}>
                              {label}
                            </option>
                          ))}
                        </Form.Control>
                      </td>
                      <td>
                        <Form.Group>
                          <Form.Check
                            type='switch'
                            name='usesTobacco'
                            id={`usesTobacco_${inf.id}`}
                            checked={inf.usesTobacco}
                            onChange={(e) => {
                              const {checked} = e.target
                              setArrPeople((prevPeople) =>
                                prevPeople.map((person) =>
                                  person.id === inf.id ? {...person, usesTobacco: checked} : person
                                )
                              )
                            }}
                          />
                        </Form.Group>
                      </td>
                      <td>
                        <Form.Check
                          type='switch'
                          name='isPregnant'
                          id={`isPregnant_${inf.id}`}
                          checked={inf.isPregnant}
                          onChange={(e) => {
                            const {checked} = e.target
                            setArrPeople((prevPeople) =>
                              prevPeople.map((person) =>
                                person.id === inf.id ? {...person, isPregnant: checked} : person
                              )
                            )
                          }}
                        />
                      </td>
                      <td>
                        <Form.Check
                          type='switch'
                          name='aptcEligible'
                          id={`aptcEligible_${inf.id}`}
                          checked={inf.aptcEligible}
                          onChange={(e) => {
                            const {checked} = e.target
                            setArrPeople((prevPeople) =>
                              prevPeople.map((person) =>
                                person.id === inf.id ? {...person, aptcEligible: checked} : person
                              )
                            )
                          }}
                        />
                      </td>
                      {showMore && (
                        <>
                          <td className='show-more'>
                            <Form.Check
                              type='switch'
                              name='isParent'
                              id={`isParent_${inf.id}`}
                              checked={inf.isParent}
                              onChange={(e) => {
                                const {checked} = e.target
                                setArrPeople((prevPeople) =>
                                  prevPeople.map((person) =>
                                    person.id === inf.id ? {...person, isParent: checked} : person
                                  )
                                )
                              }}
                            />
                          </td>

                          <td className='show-more'>
                            <Form.Check
                              type='switch'
                              name='hasMec'
                              id={`hasMec${inf.id}`}
                              checked={inf.hasMec}
                              onChange={(e) => {
                                const {checked} = e.target
                                setArrPeople((prevPeople) =>
                                  prevPeople.map((person) =>
                                    person.id === inf.id ? {...person, hasMec: checked} : person
                                  )
                                )
                              }}
                            />
                          </td>
                        </>
                      )}
                      <td>
                        <Button
                          variant='danger'
                          size='sm'
                          className='ms-2'
                          onClick={() => {
                            setArrPeople((prev) => prev.filter((person) => person.id !== inf.id))
                          }}
                        >
                          Delete
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className='mt-2'>
            <Button size='sm' variant='info' type='button' onClick={handleAddPeople}>
              Add Member
            </Button>
          </div>

          <Row className='mt-4'>
            {/* <Col lg={4}>
                <Form.Group>
                  <Form.Label>Market</Form.Label>
                  <Form.Control
                    as='select'
                    name='market'
                    value={formData.market}
                    onChange={handleChange}
                  >
                    <option value='Individual'>Individual</option>
                  </Form.Control>
                </Form.Group>
              </Col> */}
          </Row>

          {/* <Row className='mt-4'>
              <Col lg={3}>
                <Form.Group>
                  <Form.Label>Limit</Form.Label>
                  <Form.Control
                    type='number'
                    name='limit'
                    value={formData.limit}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
              <Col lg={3}>
                <Form.Group>
                  <Form.Label>Offset</Form.Label>
                  <Form.Control
                    type='number'
                    name='offset'
                    value={formData.offset}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
            </Row> */}
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' type='button' onClick={onClose}>
            Close
          </Button>
          <Button
            variant='primary'
            type='submit'
            disabled={
              checkingUsLocation ||
              !(
                (
                  formData.countyfips &&
                  formData.zipCode && // Validar que zipCode no esté vacío
                  Number(formData.householdIncome.replaceAll(',', '')) > 0 && // Validar que householdIncome sea mayor a 0
                  (formData.personOne.age || formData.personOne.dob)
                ) // Validar que exista age o dob en personOne
              )
            }
          >
            {checkingUsLocation ? <><Spinner animation='border' size='sm' className='me-2' />Searching...</> : 'Search'}
          </Button>
        </Modal.Footer>
      </Form>
      )}
      {/* ) : (
        <>
          <Modal.Body style={{backgroundColor: '#f8f8f8'}}>
            <div style={{minHeight: '600px'}}>
              <div className='mb-4'>
                <button
                  className='d-flex align-items-center gap-2 bg-light-primary rounded'
                  style={{border: 0}}
                  onClick={() => {
                    setPage('form')
                    setResult(null)
                  }}
                >
                  <i className='bi bi-arrow-left' style={{color: 'inherit'}} />
                  Volver
                </button>
              </div>
              {result && <ResultsAddQuote result={result} />}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button onClick={onClose}>Acept</Button>
          </Modal.Footer>
        </>
      )} */}
    </Modal>

    <Modal show={Boolean(acaStateNode)} onHide={() => setAcaStateNode(null)} centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <i className='bi bi-info-circle-fill text-warning me-2' />
          State Exchange — {acaStateNode?.code}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className='mb-3'>
          <strong>{acaStateNode?.name} ({acaStateNode?.code})</strong> has its own state health
          marketplace. To quote this policy you must access the state portal directly:
        </p>
        {acaStateNode?.urlAcaQuote && (
          <a
            href={acaStateNode.urlAcaQuote}
            target='_blank'
            rel='noopener noreferrer'
            className='btn btn-success w-100'
          >
            <i className='bi bi-box-arrow-up-right me-2' />
            Go to {acaStateNode.code} portal
          </a>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant='secondary' onClick={() => setAcaStateNode(null)}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>

    <Modal show={showOutsideUsModal} onHide={() => setShowOutsideUsModal(false)} centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <i className='bi bi-exclamation-triangle-fill text-danger me-2' />
          Access Restricted Outside the U.S.
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className='mb-2'>
          Per CMS (HealthCare.gov / Marketplace) agreements, agents and brokers are{' '}
          <strong>prohibited from accessing CMS systems</strong> (including Enhanced Direct
          Enrollment / ACA marketplace tools) while located outside of the United States or its
          territories, including through VPNs or other masking technologies.
        </p>
        <p className='mb-0'>
          It looks like your current connection is outside of the U.S., so this ACA search cannot
          be performed from your current location.
        </p>
      </Modal.Body>
      <Modal.Footer>
        <Button variant='secondary' onClick={() => setShowOutsideUsModal(false)}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
    </>
  )
}
